import React, { useState, useEffect, useCallback } from "react";
import RecalcPlacingsBox from "../components/RecalcPlacingsBox";
import MatchPlayersEditor from "../components/MatchPlayersEditor";
import EventMessages from "../components/EventMessages";
import HelpGuide from "../components/HelpGuide";
import RefereeAsk from "../components/RefereeAsk";
import { RefereeMark } from "../SportIcons";
import ClubRulesAdmin from "../components/ClubRulesAdmin";
import Wordmark from "../components/Wordmark";
import VersionTag from "../components/VersionTag";
import NightStandings from "../components/NightStandings";
import { webNotificationPermission, notificationsGranted } from "../nativePush";
import {
  collection, query, getDocs, getDoc, doc, setDoc, updateDoc, deleteDoc,
  orderBy, onSnapshot, Timestamp, increment, deleteField
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, app, registerFcmToken, duprDeleteMatch, duprSubmitEventMatches, sendPlayerInviteLink, listClubRolesFn, setClubRoleFn } from "../firebase";
import OrganizerRoleToggle from "../components/OrganizerRoleToggle";
import { MessagesMode } from "../types";
import MessagesModeSelect from "../components/MessagesModeSelect";
import { AuthSession, ClubEvent, Player, Participant, Game, Sport, Season, getRatingInfo, gameResult } from "../types";
import { americanoRound, americanoRoundCount, balanceSides, rotateForEvent, worstSideBias, roundsToCompleteCycle, AmericanoGame } from "../utils/americano";
import { sideNames } from "../utils/sides";
import { useClubSeasons } from "../useClubSeasons";
import CalendarButtons from "../components/CalendarButtons";
import { PickleballMark, PickleballBall } from "../SportIcons";
import { mobilePayLink, normalizeLipas } from "../utils/mobilepay";
import { APP_VERSION } from "../version";
import Avatar from "../components/Avatar";
import VenuePicker, { useClubVenues } from "../components/VenuePicker";
import VenueSettings from "../components/VenueSettings";
import SeasonSettings from "../components/SeasonSettings";
import { AnnouncementsAdmin, AnnouncementsList } from "../components/Announcements";
import NewsFeed from "../components/NewsFeed";
import { deleteEventDeep, writeNightResults } from "../utils/events";
import { closeEventConfirmText, nightResults } from "../utils/closeEvent";

// ── Tyylit ──────────────────────────────────────────────────
const C = {
  card: "var(--sl-card)",
  navy:  "var(--sl-primary)", gold: "var(--sl-accent)", text: "var(--sl-text)",
  muted: "var(--sl-muted)", bg: "var(--sl-bg)",  white: "#ffffff",
  border:"var(--sl-border)", accent: "var(--sl-success)", error: "var(--sl-error)",
  soft:  "var(--sl-soft)",
};
const courtPattern = `<svg width="400" height="400" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg"><g stroke="var(--sl-primary)" stroke-opacity="0.035" stroke-width="1.5" fill="none"><rect x="20" y="20" width="360" height="360" rx="24"/><rect x="60" y="60" width="280" height="280" rx="16"/><line x1="200" y1="60" x2="200" y2="340"/><line x1="60" y1="140" x2="340" y2="140"/><line x1="60" y1="260" x2="340" y2="260"/><line x1="20" y1="200" x2="200" y2="20"/><line x1="200" y1="380" x2="380" y2="200"/></g></svg>`;
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };

const card: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 16, padding: 18, marginBottom: 14,
  boxShadow: "0 4px 16px rgba(22,59,104,0.06)",
};
const btn: React.CSSProperties = {
  padding: "10px 18px", borderRadius: 10, border: "none",
  cursor: "pointer", fontWeight: 700, fontSize: 13,
  fontFamily: F.body,
};
const input: React.CSSProperties = {
  width: "100%", padding: "12px 14px", boxSizing: "border-box",
  background: C.card, border: `1.5px solid ${C.border}`,
  borderRadius: 10, fontSize: 14, color: C.text,
  fontFamily: F.body, outline: "none", marginBottom: 10,
};

type AdminTab = "events" | "players" | "history" | "news" | "settings";

// ── Apufunktio: PIN-generointi ────────────────────────────────
function genPin(): string {
  return String(Math.floor(1000 + Math.random() * 9000));
}

// ── Kalenteri-apufunktiot ─────────────────────────────────────

// ── AdminView ─────────────────────────────────────────────────
interface AdminViewProps {
  session: AuthSession;
  onLogout: () => void;
}

export default function AdminView({ session, onLogout }: AdminViewProps) {
  const [showHelp, setShowHelp] = useState(false);
  const closeHelp = useCallback(() => setShowHelp(false), []);
  const [showReferee, setShowReferee] = useState(false);
  const closeReferee = useCallback(() => setShowReferee(false), []);
  const [tab, setTab]           = useState<AdminTab>("events");
  const [events, setEvents]     = useState<ClubEvent[]>([]);
  const [players, setPlayers]   = useState<Player[]>([]);
  const [loading, setLoading]   = useState(true);
  const [clubLogo, setClubLogo] = useState<string>("");
  const [dark, setDark]         = useState(() => localStorage.getItem("spinloop_dark") === "1");
  // Teema on CSS-muuttujissa (src/theme.css); tämä vaihtaa arvot koko dokumentille.
  useEffect(() => { document.documentElement.dataset.theme = dark ? "dark" : "light"; }, [dark]);
  const toggleDark = () => setDark(d => { const n = !d; localStorage.setItem("spinloop_dark", n ? "1" : "0"); return n; });
  const [lang, setLang]         = useState<"fi"|"en">(() => (localStorage.getItem("spinloop_lang") as "fi"|"en") || "fi");
  const toggleLang = () => setLang(l => { const n = l === "fi" ? "en" : "fi"; localStorage.setItem("spinloop_lang", n); return n; });

  const T = dark ? {
    wrapBg:      "linear-gradient(160deg, #0d2540 0%, #1a3a6b 100%)",
    headerBg:    "rgba(10,28,54,0.97)",
    headerBorder:"1px solid rgba(255,255,255,0.08)",
    headerShadow:"0 2px 20px rgba(0,0,0,0.3)",
    logoColor:   "#ffffff",
    subtitleColor:"rgba(255,255,255,0.50)",
    logoutBg:    "rgba(255,255,255,0.12)",
    logoutColor: "#ffffff",
    navBg:       "rgba(10,28,54,0.97)",
    navBorder:   "1px solid rgba(255,255,255,0.08)",
    navShadow:   "0 -4px 24px rgba(0,0,0,0.4)",
    tabActive:   C.gold,
    tabInactive: "rgba(255,255,255,0.40)",
  } : {
    wrapBg:      C.bg,
    headerBg:    C.white,
    headerBorder:`1px solid ${C.border}`,
    headerShadow:"0 2px 12px rgba(22,59,104,0.08)",
    logoColor:   C.navy,
    subtitleColor:C.muted,
    logoutBg:    C.soft,
    logoutColor: C.navy,
    navBg:       C.white,
    navBorder:   `1px solid ${C.border}`,
    navShadow:   "0 -4px 20px rgba(22,59,104,0.10)",
    tabActive:   C.navy,
    tabInactive: C.muted,
  };
  const [showQuickWelcome, setShowQuickWelcome] = useState(
    () => !!(session as any)._quickEventPin
  );
  const quickEventPin  = (session as any)._quickEventPin  as string | undefined;
  const quickAdminPin  = (session as any)._quickAdminPin  as string | undefined;

  // ── PIN-vaihto: muistutus, ei joka kirjautumisen este ────────
  // Se onko klubin PIN vaihdettu on klubin ominaisuus, ei selaimen.
  // Ennen tieto asui pelkässä localStoragessa, joten sama kysymys
  // palasi jokaisella uudella laitteella ja appin uudelleenasennuksen
  // jälkeen. Nyt totuus luetaan klubidokumentin pinChangedAt-kentästä
  // ja localStorage on enää paikallinen välimuisti.
  const PIN_CHANGED_KEY = `spinloop_pin_changed_${session.clubId}`;
  const PIN_SNOOZE_KEY  = `spinloop_pin_snooze_${session.clubId}`;
  const PIN_SNOOZE_DAYS = 14;
  const [pinChanged, setPinChanged] = useState<boolean | null>(
    () => (localStorage.getItem(PIN_CHANGED_KEY) ? true : null)
  );
  const [showPinModal, setShowPinModal] = useState(false);
  // Tapahtumalistan pituus: seuraavat N, loput napin takana. Oletus 3.
  const [eventLimit, setEventLimit] = useState(3);

  // ── Lataa klubin logo ja PIN-tila ───────────────────────────
  useEffect(() => {
    getDoc(doc(db, "clubs", session.clubId)).then(snap => {
      const data = snap.data() || {};
      if (data.logoUrl) setClubLogo(data.logoUrl);
      if (typeof data.eventListLimit === "number") setEventLimit(data.eventListLimit);

      const changed = !!data.pinChangedAt || !!localStorage.getItem(PIN_CHANGED_KEY);
      setPinChanged(changed);
      if (changed) { localStorage.setItem(PIN_CHANGED_KEY, "1"); return; }

      // PIN on yhä se väliaikainen. Muistutetaan, mutta korkeintaan
      // kahden viikon välein — joka kirjautumisella kysyminen opetti
      // vain ohittamaan modaalin lukematta sitä.
      const snoozed = Number(localStorage.getItem(PIN_SNOOZE_KEY) || 0);
      if (Date.now() - snoozed < PIN_SNOOZE_DAYS * 86400000) return;
      setShowPinModal(true);
    }).catch(() => {});
  }, [session.clubId, PIN_CHANGED_KEY, PIN_SNOOZE_KEY]);

  // ── Rekisteröi FCM-token (admin) ────────────────────────────
  useEffect(() => {
    registerFcmToken("admin", session.clubId, "admin").catch(() => {});
  }, [session.clubId]);

  // ── Lataa tapahtumat ────────────────────────────────────────
  const loadEvents = useCallback(async () => {
    const snap = await getDocs(
      query(collection(db, "clubs", session.clubId, "events"), orderBy("createdAt", "desc"))
    );
    setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() } as ClubEvent)));
  }, [session.clubId]);

  // ── Lataa pelaajat ──────────────────────────────────────────
  const loadPlayers = useCallback(async () => {
    const snap = await getDocs(
      query(collection(db, "clubs", session.clubId, "players"), orderBy("name"))
    );
    setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() } as Player)));
  }, [session.clubId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadEvents(), loadPlayers()]).finally(() => setLoading(false));
  }, [loadEvents, loadPlayers]);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <div style={{ fontSize: 14, color: C.muted }}>{lang === "fi" ? "Ladataan..." : "Loading..."}</div>
    </div>
  );

  const bottomTabs = [
    { key: "events",   icon: "📅", label: lang === "fi" ? "Tapahtumat" : "Events"   },
    { key: "players",  icon: "👥", label: lang === "fi" ? "Pelaajat"   : "Players"  },
    { key: "history",  icon: "🏆", label: lang === "fi" ? "Historia"   : "History"  },
    { key: "news",     icon: "📰", label: lang === "fi" ? "Uutiset"    : "News"     },
    { key: "settings", icon: "⚙️", label: lang === "fi" ? "Asetukset"  : "Settings" },
  ] as { key: AdminTab; icon: string; label: string }[];

  return (
    <div style={{ background: T.wrapBg, minHeight: "100vh", maxWidth: 520, margin: "0 auto", fontFamily: F.body, position: "relative" }}>
      {/* Court pattern */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(courtPattern)}")`,
        backgroundRepeat: "repeat", backgroundSize: "400px",
      }} />
      {/* Pallo vesileimana oikeassa alakulmassa, osin ruudun ulkopuolella.
          Keskellä se kilpaili sisällön kanssa; kulmassa se on tunnelma, ei
          tausta. Kiinnitetty, jotta se ei vieri sisällön mukana. */}
      <img src="/icons/pickleball.png" alt="" aria-hidden="true" style={{
        position: "fixed", right: -140, bottom: -120, width: 460, height: 460,
        opacity: dark ? 0.10 : 0.08, transform: "rotate(-14deg)",
        pointerEvents: "none", zIndex: 0, userSelect: "none",
      }} />

      {/* Sticky header logolla */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: T.headerBg,
        borderBottom: T.headerBorder,
        boxShadow: T.headerShadow,
        padding: "14px 20px",
        // iOS: Dynamic Island peittaisi palkin ilman turva-aluetta
        paddingTop: "calc(14px + env(safe-area-inset-top))",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img src={clubLogo || "/logo192.png"} alt="logo" style={{ width: 56, height: 56, borderRadius: 12, objectFit: "contain" }} />
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, fontFamily: F.heading, letterSpacing: 2, lineHeight: 1, color: T.logoColor }}>
              <Wordmark />
            </div>
            <div style={{ fontSize: 12, color: T.subtitleColor, marginTop: 3, fontFamily: F.body }}>
              {lang === "fi" ? "Hallitse pelaajia ja panoksia" : "Manage players and stakes"}
            </div>
            {/* Versio omalla rivillään. Alaotsikon perässä samalla rivillä
                kokeiltiin, ja se oli huonompi: teksti ei mahtunut 360–375 px
                leveydelle, rivi kiertyi ja yläpalkki kasvoi 8 px. Omalla
                rivillään kasvu on 4 px, koska vieressä oleva 56 px logo
                määrää palkin korkeuden lähes kokonaan. Mitattu, ei arvioitu. */}
            <VersionTag lang={lang} color={T.subtitleColor} style={{ display: "block", marginTop: 2 }} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button onClick={toggleDark} style={{ ...btn, background: T.logoutBg, color: T.logoutColor, fontSize: 16, padding: "5px 10px", borderRadius: 20 }} title="Vaihda teema">
            {dark ? "☀️" : "🌙"}
          </button>
          <button onClick={toggleLang} style={{ ...btn, background: T.logoutBg, color: T.logoutColor, fontSize: 16, padding: "5px 10px", borderRadius: 20 }} title="Language">
            {lang === "fi" ? "🌐 EN" : "🌐 FI"}
          </button>
          <button onClick={() => setShowHelp(true)} style={{ ...btn, background: T.logoutBg, color: T.logoutColor, fontSize: 11, padding: "7px 12px", borderRadius: 20 }}>
            {lang === "fi" ? "Ohjeet" : "Help"}
          </button>
          <button onClick={onLogout} style={{ ...btn, background: T.logoutBg, color: T.logoutColor, fontSize: 11, padding: "7px 14px", borderRadius: 20 }}>
            {lang === "fi" ? "Ulos" : "Sign out"}
          </button>
        </div>
      </div>


      {/* PIN-vaihto modal */}
      {showPinModal && !showQuickWelcome && (
        <ChangePinModal
          clubId={session.clubId}
          lang={lang}
          isFirstLogin={pinChanged === false}
          onDone={() => {
            localStorage.setItem(PIN_CHANGED_KEY, "1");
            localStorage.removeItem(PIN_SNOOZE_KEY);
            setPinChanged(true);
            setShowPinModal(false);
          }}
          onSkip={() => {
            // Ilman aikaleimaa "vaihda myöhemmin" tarkoitti käytännössä
            // "kysy uudestaan heti seuraavalla kirjautumisella".
            localStorage.setItem(PIN_SNOOZE_KEY, String(Date.now()));
            setShowPinModal(false);
          }}
        />
      )}

      {/* Pikatapahtuma — tervetulokortti */}
      {showQuickWelcome && quickEventPin && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: "rgba(22,59,104,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}>
          <div style={{ background: C.card, borderRadius: 24, padding: 28, maxWidth: 360, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.navy, fontFamily: F.heading, marginBottom: 4 }}>
              {lang === "en" ? "Event created!" : "Tapahtuma luotu!"}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
              {lang === "en" ? "Share these codes with players" : "Jaa nämä koodit kavereille"}
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              <div style={{ flex: 1, background: C.bg, borderRadius: 14, padding: "14px 10px" }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                  {lang === "en" ? "Club code" : "Klubikoodi"}
                </div>
                <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "monospace", letterSpacing: 2, color: C.navy }}>{session.clubCode}</div>
              </div>
              <div style={{ flex: 1, background: C.bg, borderRadius: 14, padding: "14px 10px" }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                  {lang === "en" ? "Event PIN" : "Tapahtuman PIN"}
                </div>
                <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "monospace", letterSpacing: 2, color: C.gold }}>{quickEventPin}</div>
              </div>
            </div>

            <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
              <div style={{ fontSize: 12, color: "#0369a1", fontWeight: 700, marginBottom: 4 }}>
                {lang === "en" ? "How players join:" : "Miten kaverit liittyvät:"}
              </div>
              <div style={{ fontSize: 12, color: "#0369a1", lineHeight: 1.7 }}>
                1. {lang === "en" ? "Go to" : "Mene"} <strong>spinloop.app</strong><br/>
                2. {lang === "en" ? "Enter club code" : "Syötä klubikoodi"} <strong>{session.clubCode}</strong><br/>
                3. {lang === "en" ? "Choose Player → enter PIN" : "Valitse Pelaaja → syötä PIN"} <strong>{quickEventPin}</strong>
              </div>
            </div>

            <button
              onClick={() => {
                const text = lang === "en"
                  ? `Spinloop event!\n\nspinloop.app\nClub code: ${session.clubCode}\nPIN: ${quickEventPin}`
                  : `Spinloop-tapahtuma!\n\nspinloop.app\nKlubikoodi: ${session.clubCode}\nPIN: ${quickEventPin}`;
                if (navigator.share) navigator.share({ title: "Spinloop", text });
                else navigator.clipboard.writeText(text);
                setShowQuickWelcome(false);
              }}
              style={{ ...btn, width: "100%", background: C.navy, color: C.white, marginBottom: 10, fontSize: 15 }}
            >
              📤 {lang === "en" ? "Share code with players" : "Jaa koodi kavereille"}
            </button>
            <button onClick={() => setShowQuickWelcome(false)}
              style={{ ...btn, width: "100%", background: C.soft, color: C.navy, fontSize: 13 }}>
              {lang === "en" ? "Continue →" : "Jatka →"}
            </button>
          </div>
        </div>
      )}

      {/* Sisältö */}
      {/*
        Ei zIndexia tahan. `position: relative` + `zIndex` luo oman
        pinoamiskontekstin, ja silloin kaikki sisalla olevat z-indeksit ovat
        suhteessa vain siihen: valilehden sisalla avautuva modaali (zIndex 300)
        jai ylapalkin ja alanavigaation (zIndex 100) alle, koska kaare itse oli
        tasolla 1. Ilman zIndexia `relative` ei luo kontekstia, ja modaali
        kilpailee palkkien kanssa samalla tasolla kuten on tarkoitus.

        Sisalto pysyy silti kenttakuvion (zIndex 0, kiinnitetty) paalla:
        z-index:auto ja z-index:0 maalataan samassa vaiheessa DOM-jarjestyksessa,
        ja tama kaare on kuviota myohemmin.
      */}
      <div style={{ position: "relative", padding: "16px 16px calc(90px + var(--sl-safe-bottom))" }}>
        {tab === "events"   && <EventsTab   clubId={session.clubId} events={events}  players={players} onRefresh={loadEvents} lang={lang} limit={eventLimit} />}
        {tab === "players"  && <PlayersTab  clubId={session.clubId} clubName={session.clubName} players={players} onRefresh={loadPlayers} lang={lang} />}
        {tab === "history"  && <HistoryTab  clubId={session.clubId} events={events} lang={lang} />}
        {/* Sama näkymä kuin pelaajalla: tiedotteet kirjoitetaan Asetuksissa. */}
        {tab === "news"     && (
          <div>
            <AnnouncementsList clubId={session.clubId} lang={lang} />
            <NewsFeed lang={lang} />
          </div>
        )}
        {tab === "settings" && (
          <SettingsTab clubId={session.clubId} lang={lang} onChangePinClick={() => setShowPinModal(true)} onRefresh={loadEvents}
            eventLimit={eventLimit}
            onEventLimit={async n => {
              setEventLimit(n);
              try { await updateDoc(doc(db, "clubs", session.clubId), { eventListLimit: n }); }
              catch (err) { console.error("eventListLimit", err); }
            }} />
        )}
      </div>

      {showHelp && <HelpGuide role="club_admin" lang={lang} onClose={closeHelp} />}
      {showReferee && <RefereeAsk lang={lang} onClose={closeReferee} />}

      {/* Bottom navigation */}
      <div style={{
        position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "100%", maxWidth: 520, zIndex: 100,
        background: T.navBg, borderTop: T.navBorder,
        boxShadow: T.navShadow,
        display: "flex",
        // Järjestelmäpainikkeiden / kotipalkin alue (theme.css).
        paddingBottom: "var(--sl-safe-bottom)",
      }}>
        {bottomTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex: 1, margin: "6px 3px 10px", padding: "7px 0 6px", border: "none", cursor: "pointer",
            borderRadius: 14, background: tab === t.key ? "var(--sl-soft)" : "none",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
          }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>{t.icon}</span>
            <span style={{
              fontSize: 10, fontWeight: tab === t.key ? 700 : 600, fontFamily: F.body,
              color: tab === t.key ? T.tabActive : T.tabInactive,
            }}>{t.label}</span>
          </button>
        ))}
        {/* Sama kuvio kuin pelaajan puolella: tuomari ei ole valilehti
            vaan avaa nakyman, joten ei aktiivista taustaa. */}
        <button onClick={() => setShowReferee(true)} style={{
          flex: 1, margin: "6px 3px 10px", padding: "7px 0 6px", border: "none", cursor: "pointer",
          borderRadius: 14, background: "none",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
        }}>
          <RefereeMark size={22} />
          <span style={{
            fontSize: 10, fontWeight: 600, fontFamily: F.body, color: T.tabInactive,
          }}>{lang === "fi" ? "Tuomari" : "Referee"}</span>
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// EVENTS TAB
// ══════════════════════════════════════════════════════════════
export function EventsTab({ clubId, events, players, onRefresh, lang = "fi", limit = 3, organizer = false }: {
  clubId: string; events: ClubEvent[]; players: Player[]; onRefresh: () => void; lang?: "fi"|"en";
  /** Montako tulevaa näytetään ennen "Näytä kaikki" -nappia. */
  limit?: number;
  /**
   * Järjestäjän näkymä (docs/roolit.md): kutsut, ottelut, tulokset ja
   * illan päättäminen, mutta ei tapahtuman luontia, muokkausta, poistoa
   * eikä DUPR-lähetystä. Säännöt rajaavat saman palvelimella.
   */
  organizer?: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sport, setSport]       = useState<Sport>("pickleball");
  const [venue, setVenue]       = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [panos, setPanos]       = useState("0");
  // Liiga: käsin luotu tapahtuma voi kuulua kauteen tai olla sen ulkopuolella.
  // Kauden generaattori asettaa seasonId:n itse, joten arjessa tätä ei kosketa.
  const { seasons } = useClubSeasons(clubId);
  const openSeasons = seasons.filter(s => s.status === "open");
  const [seasonId, setSeasonId] = useState("");
  const [messagesMode, setMessagesMode] = useState<MessagesMode>("verified");
  const seasonNameOf = (id?: string) => seasons.find(s => s.id === id)?.name;
  const [datetime, setDatetime] = useState(() => {
    // datetime-local odottaa paikallista aikaa muodossa YYYY-MM-DDTHH:mm.
    //
    // Aiemmin tassa oli d.toISOString().slice(0, 16), joka muuntaa UTC:hen
    // ja pudottaa Z:n pois. Kentta tulkitsi sen silti paikalliseksi, joten
    // oletusaika oli kolme tuntia vaarassa kesaaikaan ja kaksi talvella.
    // Huomaamatta jaanyt oletus loi tapahtuman vaaraan kellonaikaan.
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  async function createEvent() {
    const pin  = genPin();
    const ref  = doc(collection(db, "clubs", clubId, "events"));
    await setDoc(ref, {
      sport, venue: venue.trim(), venueAddress: venueAddress.trim(),
      panos: Number(panos), datetime,
      pin, gameType: "Americano", status: "open",
      ...(seasonId ? { seasonId } : {}),
      messages: messagesMode,
      createdAt: new Date().toISOString(),
    });
    setCreating(false);
    setSport("pickleball"); setVenue(""); setVenueAddress(""); setPanos("0"); setSeasonId(""); setMessagesMode("verified");
    onRefresh();
  }

  async function closeEvent(eventId: string) {
    // Hae tapahtuman pelit, osallistujat ja tapahtuman tiedot
    const evRef = doc(db, "clubs", clubId, "events", eventId);
    const [gSnap, pSnap, evSnap] = await Promise.all([
      getDocs(collection(db, "clubs", clubId, "events", eventId, "games")),
      getDocs(collection(db, "clubs", clubId, "events", eventId, "participants")),
      getDoc(evRef),
    ]);
    const sport = evSnap.data()?.sport ?? "pickleball";

    const games = gSnap.docs.map(d => d.data() as Game);
    const parts = pSnap.docs.map(d => ({ id: d.id, ...d.data() } as Participant));

    // Illan sijoitus ja läsnäolo talteen osallistujaan. Liigan taulukko
    // lasketaan näistä (docs/liiga.md). Sama lasku kuin päätetyn illan
    // uudelleenlaskennassa (nightResults, src/utils/closeEvent.ts).
    //
    // Voitot, häviöt ja pelatut pelit tulevat samasta rankPlayers-laskusta
    // kuin sijoitus. Aiemmin tässä oli oma rinnakkainen lasku, joka laski
    // 0–0:n ja tasapelin voitoiksi toiselle parille; nyt molemmat
    // noudattavat gameResult-sääntöä eivätkä voi erota toisistaan.
    const rows = nightResults(games, parts);
    const failedRows = await writeNightResults(clubId, eventId, rows);

    // Päivitä voitot/häviöt Firestoreen — EI ylikirjoiteta manuaalista DUPR-lukua
    //
    // Yhden pelaajan epäonnistunut kirjoitus ei saa estää tapahtuman
    // päättämistä. Aiemmin nämä olivat kattamattomassa Promise.allissa, joten
    // esimerkiksi puuttuva pelaajadokumentti hylkäsi koko ketjun eikä alla
    // olevaa status-riviä ajettu koskaan — nappi näytti siltä ettei se tee
    // yhtään mitään, eikä virheestä kerrottu missään.
    const failed: string[] = [...failedRows];
    await Promise.all(rows.map(async ({ participant: p, standing: st, played }) => {
      const wins = st?.wins ?? 0, losses = st?.losses ?? 0;
      if (!played) return;
      if (!p.playerId) return;
      try {
        // Padel ja tennis: päivitä paikallinen rating (ei DUPR-integraatiota)
        // Pickleball: DUPR-luku tulee DUPR API:sta — ei lasketa paikallisesti
        if (sport === "padel" || sport === "tennis") {
          const info = getRatingInfo(sport as any, wins, losses);
          const ratingField = sport === "padel" ? "padel" : "tennis";
          await updateDoc(doc(db, "clubs", clubId, "players", p.playerId), {
            wins: increment(wins), losses: increment(losses),
            [`ratings.${ratingField}`]: info.value,
          });
        } else {
          await updateDoc(doc(db, "clubs", clubId, "players", p.playerId), {
            wins: increment(wins), losses: increment(losses),
          });
        }
      } catch (err) {
        failed.push(p.displayName);
        console.error("closeEvent: tilastoa ei voitu päivittää", p.displayName, err);
      }
    }));

    try {
      await updateDoc(doc(db, "clubs", clubId, "events", eventId), { status: "finished" });
    } catch (err) {
      console.error("closeEvent: tapahtumaa ei voitu päättää", err);
      window.alert(lang === "en"
        ? "Could not end the event. Check your connection and try again."
        : "Tapahtumaa ei voitu päättää. Tarkista yhteys ja yritä uudelleen.");
      return;
    }

    if (failed.length > 0) {
      window.alert(lang === "en"
        ? `Event ended, but stats could not be updated for: ${failed.join(", ")}`
        : `Tapahtuma päätettiin, mutta tilastoja ei voitu päivittää: ${failed.join(", ")}`);
    }
    onRefresh();
  }

  // Peruutus on eri asia kuin päättäminen: tapahtumaa ei ollut, joten
  // pelaajien tilastoihin ei kirjata mitään. Ilman tätä ainoa tapa päästä
  // eroon peruuntuneesta tapahtumasta oli päättää se, jolloin arvotut
  // ottelut menivät tilastoihin oikeina tuloksina.
  async function cancelEvent(eventId: string) {
    if (!window.confirm(lang === "en"
      ? "Cancel this event? Matches and participants are deleted, and nothing is recorded in player stats."
      : "Peruutetaanko tapahtuma? Ottelut ja osallistujat poistetaan, eikä pelaajien tilastoihin kirjata mitään.")) return;
    try {
      await deleteEventDeep(clubId, eventId);
    } catch (err) {
      console.error("cancelEvent", err);
      window.alert(lang === "en"
        ? "Could not cancel the event. Check your connection and try again."
        : "Tapahtumaa ei voitu peruuttaa. Tarkista yhteys ja yritä uudelleen.");
      return;
    }
    onRefresh();
  }

  // Aikajärjestys, seuraava ylimpänä. Luontijärjestys sekoitti kauden
  // illat, jotka syntyvät samalla hetkellä. Käynnissä oleva aina ensin.
  const activeEvents = events
    .filter(e => e.status !== "finished")
    .sort((a, b) => {
      if ((a.status === "active") !== (b.status === "active")) return a.status === "active" ? -1 : 1;
      return (a.datetime || "").localeCompare(b.datetime || "");
    });
  const visibleActive = showAll ? activeEvents : activeEvents.slice(0, Math.max(1, limit));
  const hiddenCount = activeEvents.length - visibleActive.length;
  const finishedEvents = events.filter(e => e.status === "finished");

  return (
    <div>
      {/* Luo tapahtuma */}
      {organizer ? null : !creating ? (
        <button onClick={() => setCreating(true)} style={{
          ...btn, width: "100%", padding: "14px 0", marginBottom: 16,
          background: `linear-gradient(135deg, ${C.navy}, #2a5c8f)`,
          color: C.white, fontSize: 15,
        }}>
          {lang === "fi" ? "+ Luo uusi tapahtuma" : "+ Create new event"}
        </button>
      ) : (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 14, fontFamily: F.heading }}>
            {lang === "fi" ? "UUSI TAPAHTUMA" : "NEW EVENT"}
          </div>
          {/* Lajivalitsin poistettu — uudet tapahtumat ovat aina pickleballia */}
          <VenuePicker clubId={clubId} lang={lang} inputStyle={input}
            value={{ venue, address: venueAddress }}
            onChange={v => { setVenue(v.venue); setVenueAddress(v.address); }} />
          <input value={datetime} onChange={e => setDatetime(e.target.value)} type="datetime-local" style={input} />
          {openSeasons.length > 0 && (
            <select value={seasonId} onChange={e => setSeasonId(e.target.value)}
              style={{ ...input, appearance: "auto" as React.CSSProperties["appearance"] }}>
              <option value="">{lang === "fi" ? "Ei liigaa — tavallinen pelivuoro" : "No league — regular session"}</option>
              {openSeasons.map(s => <option key={s.id} value={s.id}>🏆 {s.name}</option>)}
            </select>
          )}
          <div style={{ fontSize: 12, color: "var(--sl-muted)", marginBottom: 4 }}>{lang === "fi" ? "Panos (€) — jätä 0 jos ilmainen" : "Entry fee (€) — leave 0 if free"}</div>
          <input value={panos} onChange={e => setPanos(e.target.value)} placeholder="0" type="number" min="0" style={input} />
          <MessagesModeSelect value={messagesMode} onChange={setMessagesMode} lang={lang} style={input} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createEvent} style={{ ...btn, flex: 1, background: C.navy, color: C.white }}>
              {lang === "fi" ? "Luo & generoi PIN" : "Create & generate PIN"}
            </button>
            <button onClick={() => setCreating(false)} style={{ ...btn, flex: 1, background: C.soft, color: C.text }}>
              {lang === "fi" ? "Peruuta" : "Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* Aktiiviset tapahtumat */}
      {activeEvents.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
            {lang === "fi" ? "Seuraavat" : "Upcoming"}
          </div>
          {visibleActive.map(ev => (
            <EventCard key={ev.id} event={ev} clubId={clubId} players={players} organizer={organizer}
              seasons={openSeasons} seasonName={seasonNameOf(ev.seasonId)}
              onClose={() => closeEvent(ev.id)} onCancel={() => cancelEvent(ev.id)}
              onRefresh={onRefresh} lang={lang} />
          ))}
          {(hiddenCount > 0 || showAll) && activeEvents.length > Math.max(1, limit) && (
            <button onClick={() => setShowAll(s => !s)} style={{
              ...btn, width: "100%", marginBottom: 14, background: C.soft, color: C.navy,
            }}>
              {showAll
                ? (lang === "fi" ? `Näytä vain seuraavat ${Math.max(1, limit)}` : `Show only the next ${Math.max(1, limit)}`)
                : (lang === "fi" ? `Näytä kaikki (${activeEvents.length})` : `Show all (${activeEvents.length})`)}
            </button>
          )}
        </>
      )}

      {/* Päättyneet */}
      {finishedEvents.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", margin: "16px 0 8px" }}>
            {lang === "fi" ? "Päättyneet" : "Ended"}
          </div>
          {finishedEvents.slice(0, 10).map(ev => (
            <FinishedEventCard key={ev.id} event={ev} clubId={clubId} lang={lang} organizer={organizer} />
          ))}
        </>
      )}
    </div>
  );
}

// ── FinishedEventCard ─────────────────────────────────────────
function FinishedEventCard({ event, clubId, lang = "fi", organizer = false }: { event: ClubEvent; clubId: string; lang?: "fi"|"en"; organizer?: boolean }) {
  const [expanded, setExpanded]         = useState(false);
  const [games, setGames]               = useState<Game[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [players, setPlayers]           = useState<Player[]>([]);
  const [duprLoading, setDuprLoading]   = useState(false);
  const [duprMsg, setDuprMsg]           = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;
    const unsub1 = onSnapshot(
      collection(db, "clubs", clubId, "events", event.id, "participants"),
      snap => setParticipants(snap.docs.map(d => ({ id: d.id, ...d.data() } as Participant)))
    );
    const unsub2 = onSnapshot(
      collection(db, "clubs", clubId, "events", event.id, "games"),
      snap => setGames(snap.docs.map(d => ({ id: d.id, ...d.data() } as Game)))
    );
    const unsub3 = onSnapshot(
      collection(db, "clubs", clubId, "players"),
      snap => setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() } as Player)))
    );
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [expanded, clubId, event.id]);

  const isPickleball   = event.sport === "pickleball";
  // DUPR:iin lähtevät vain ratkenneet pelit: 0–0 on pelaamaton ja
  // tasatulos ei ole pickleball-ottelun tulos (gameResult, src/types).
  const playedGames    = games.filter(g => { const r = gameResult(g); return r === "team1" || r === "team2"; });
  const playedCount    = playedGames.length;
  const unsubmittedCount = playedGames.filter(g => !g.duprMatchCode && !g.duprSubmittedAt).length;
  // duprSent = vihreä VAIN jos kaikki pelatut ottelut on lähetetty
  const duprSent  = (!!event.duprSubmitted || duprMsg?.startsWith("✅")) && unsubmittedCount === 0;
  const duprError = !event.duprSubmitted  && duprMsg?.startsWith("❌");

  // DUPR User Gating — tarkista BASIC_L1 osallistujilta
  const playerMap = new Map(players.map(p => [p.id, p]));
  const participantsWithoutL1 = participants.filter(p => {
    if (!p.playerId) return false;
    const pl = playerMap.get(p.playerId);
    if (!pl?.duprId) return false;         // ei DUPR-linkitystä → ei gateta
    return pl.duprHasBasicL1 === false;    // on DUPR mutta ei BASIC_L1
  });
  const participantsWithoutDupr = participants.filter(p => {
    if (!p.playerId) return false;
    const pl = playerMap.get(p.playerId);
    return !pl?.duprId;                    // ei linkitetty DUPR:iin ollenkaan
  });

  async function submitToDupr() {
    if (duprSent) return; // ESTÄ TUPLAKUTSU
    setDuprLoading(true);
    setDuprMsg(null);
    try {
      const fns = getFunctions(app, "us-central1");
      const fn = httpsCallable(fns, "duprSubmitEventMatches");
      const res = await fn({ clubId, eventId: event.id }) as { data: { submitted: number; skipped: number; reasons: string[] } };
      const { submitted, skipped, reasons } = res.data;
      if (submitted === 0) {
        const reason = reasons[0] || (lang === "en" ? "No matches to submit" : "Ei lähetettäviä otteluja");
        setDuprMsg(`⚠️ ${reason}`);
      } else {
        setDuprMsg(lang === "en"
          ? `✅ ${submitted} match${submitted !== 1 ? "es" : ""} sent to DUPR${skipped > 0 ? `, ${skipped} skipped` : ""}`
          : `✅ ${submitted} ottelu lähetetty DUPR:iin${skipped > 0 ? `, ${skipped} ohitettu` : ""}`);
      }
    } catch (err: unknown) {
      const msg = (err as Error).message || (lang === "en" ? "Unknown error" : "Tuntematon virhe");
      setDuprMsg(lang === "en" ? `❌ Error: ${msg}` : `❌ Virhe: ${msg}`);
    } finally {
      setDuprLoading(false);
    }
  }

  return (
    <div style={{ ...card, opacity: expanded ? 1 : 0.75 }}>
      <div onClick={() => setExpanded(!expanded)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
            <PickleballMark /> {event.venue || "Tapahtuma"}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {new Date(event.datetime).toLocaleDateString("fi-FI")}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isPickleball && duprSent && (
            <span style={{ fontSize: 10, color: "#16a34a", background: "#f0fdf4", borderRadius: 6, padding: "2px 7px", fontWeight: 700, border: "1px solid #bbf7d0" }}>
              ✅ DUPR
            </span>
          )}
          <span style={{ fontSize: 11, color: C.muted, background: C.soft, borderRadius: 6, padding: "3px 8px" }}>{lang === "fi" ? "Päättynyt" : "Ended"}</span>
          {!organizer && (
            <button onClick={async e => {
              e.stopPropagation();
              if (!window.confirm(lang === "en" ? "Delete this event?" : "Poistetaanko tapahtuma?")) return;
              await deleteEventDeep(clubId, event.id);
            }} style={{ background: "none", border: "none", cursor: "pointer", color: C.error, fontSize: 16, padding: "0 4px" }}>
              🗑
            </button>
          )}
          <span style={{ fontSize: 14, color: C.muted }}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          {games.length === 0
            ? <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: 16 }}>{lang === "fi" ? "Ei otteluja tallennettu" : "No matches saved yet"}</div>
            : <>
                <GamesSection clubId={clubId} eventId={event.id} games={games} lang={lang} participants={participants} finished venue={event.venue} seasonId={event.seasonId} />
                <Scoreboard games={games} participants={participants} lang={lang} />
              </>
          }
          <RecalcPlacingsBox clubId={clubId} event={event} games={games} participants={participants} lang={lang} />
          {/* DUPR-lähetys — vain pickleball-tapahtumille joissa on tuloksia */}
          {/* DUPR-lähetys on adminin (docs/roolit.md). */}
          {!organizer && isPickleball && playedCount > 0 && (
            <div style={{
              marginTop: 14, padding: "12px 14px", borderRadius: 10,
              background: duprSent ? "#f0fdf4" : duprError ? "#fef2f2" : "#eff6ff",
              border: `1px solid ${duprSent ? "#bbf7d0" : duprError ? "#fecaca" : "#bfdbfe"}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: duprSent ? "#16a34a" : duprError ? "#dc2626" : "#1d4ed8" }}>
                    {duprSent ? "✅" : duprError ? "❌" : "🏆"} DUPR-integraatio
                  </div>
                  <div style={{ fontSize: 11, color: duprSent ? "#16a34a" : "#3b82f6", marginTop: 2 }}>
                    {duprSent
                      ? (lang === "en" ? "All matches sent to DUPR ✓" : "Kaikki ottelut lähetetty DUPR:iin ✓")
                      : unsubmittedCount < playedCount
                        ? (lang === "en" ? `${unsubmittedCount} match${unsubmittedCount !== 1 ? "es" : ""} not yet submitted` : `${unsubmittedCount} ottelua vielä lähettämättä`)
                        : (lang === "en" ? `${playedCount} match${playedCount !== 1 ? "es" : ""} ready to submit` : `${playedCount} ottelua valmiina lähetettäväksi`)}
                  </div>
                </div>
                <button
                  onClick={submitToDupr}
                  disabled={duprLoading || !!duprSent}
                  style={{
                    ...btn, fontSize: 12, padding: "7px 14px", color: C.white,
                    background: duprLoading ? C.muted : duprSent ? "#16a34a" : duprError ? "#dc2626" : "#2563eb",
                    cursor: (duprLoading || !!duprSent) ? "default" : "pointer",
                    opacity: duprLoading ? 0.6 : 1,
                  }}
                >
                  {duprLoading
                    ? (lang === "en" ? "⏳ Submitting..." : "⏳ Lähetetään...")
                    : duprSent ? "✅ DUPR"
                    : duprError ? (lang === "en" ? "❌ Try again" : "❌ Yritä uudelleen")
                    : (lang === "en" ? "Submit to DUPR" : "Lähetä DUPR:iin")}
                </button>
              </div>
              {duprMsg && !duprSent && (
                <div style={{ fontSize: 12, marginTop: 8, color: duprMsg.startsWith("⚠️") ? "#b45309" : C.error }}>
                  {duprMsg}
                </div>
              )}
              {/* DUPR User Gating -varoitukset */}
              {!duprSent && participantsWithoutL1.length > 0 && (
                <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fcd34d" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", marginBottom: 3 }}>
                    ⚠️ {lang === "en" ? "Missing DUPR BASIC_L1 entitlement:" : "Puuttuu DUPR BASIC_L1 -oikeus:"}
                  </div>
                  {participantsWithoutL1.map(p => (
                    <div key={p.id} style={{ fontSize: 11, color: "#92400e" }}>• {p.displayName}</div>
                  ))}
                  <div style={{ fontSize: 10, color: "#b45309", marginTop: 4 }}>
                    {lang === "en"
                      ? "These players must subscribe to DUPR+ to have their matches rated."
                      : "Nämä pelaajat tarvitsevat DUPR+-tilauksen jotta ottelut kirjataan ratingiin."}
                  </div>
                </div>
              )}
              {!duprSent && participantsWithoutDupr.length > 0 && (
                <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 3 }}>
                    🔗 {lang === "en" ? "No DUPR account linked:" : "Ei DUPR-tiliä linkitetty:"}
                  </div>
                  {participantsWithoutDupr.map(p => (
                    <div key={p.id} style={{ fontSize: 11, color: C.muted }}>• {p.displayName}</div>
                  ))}
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                    {lang === "en"
                      ? "These players' matches will be skipped in DUPR submission."
                      : "Näiden pelaajien ottelut ohitetaan DUPR-lähetyksessä."}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── InviteModal ───────────────────────────────────────────────
function InviteModal({ event, participants, players, clubId, onClose, lang = "fi" }: {
  event: ClubEvent; participants: Participant[]; players: Player[];
  clubId: string; onClose: () => void; lang?: "fi"|"en";
}) {
  const clubCode = localStorage.getItem("spinloop_club_code") || "";
  const clubName = localStorage.getItem("spinloop_club_name") || "";
  const appUrl   = "https://spinloop.app";
  // Sama klubikoodi linkkiin kuin sahkopostikutsussa. Ilman tata
  // leikepoydan teksti ja WhatsApp-viesti veivat pelkalle etusivulle, joka
  // markkinoi PWA-asennusta, ja vastaanottaja sai naputella koodin kasin
  // samasta viestista. PIN jatetaan pois osoitteesta: se on salasana.
  const joinUrl  = clubCode ?
    `${appUrl}/?club=${encodeURIComponent(clubCode)}` :
    appUrl;

  const dt = new Date(event.datetime);
  const locale  = lang === "en" ? "en-GB" : "fi-FI";
  const dateStr = dt.toLocaleString(locale, { weekday:"long", day:"numeric", month:"long", hour:"2-digit", minute:"2-digit" });
  const sport   = event.sport.charAt(0).toUpperCase() + event.sport.slice(1);
  const panos   = event.panos
    ? `${event.panos} €`
    : (lang === "en" ? "Free" : "Ilmainen");

  // Kutsuttavat: ne joilla ei ole vielä vahvistusta. Jo ilmoittautunutta ei
  // kehoteta ilmoittautumaan.
  //
  // Tämä suodatin oli aiemmin vain pushTargetsissa, jolloin sähköposti lähti
  // kaikille osallistujille ja push vain kutsuttaville. Napissa luki
  // "5 sähköpostia, 2 ilmoitusta" — sama nappi kahdella eri säännöllä, ja
  // neljä ihmistä sai turhan kutsun tapahtumaan johon oli jo ilmoittautunut.
  // Vastaanottajalistaa ei voi muokata käsin, joten sääntö on ainoa suoja.
  // Itse "en pääse" vastannut (source "player") ei saa uutta kutsua.
  const invitable = participants.filter(p => p.playerId && !p.confirmed && p.source !== "player");

  // Etsi sähköpostit kutsuttaville
  const withEmail = invitable
    .map(p => {
      const pl = players.find(x => x.id === p.playerId);
      return { name: p.displayName, email: pl?.email || "", phone: pl?.phone || "", playerId: p.playerId || "" };
    });
  const emails = withEmail.filter(x => x.email).map(x => x.email);
  // Push tavoittaa myös ne joilta puuttuu sähköposti — he jäivät ennen
  // kutsun ulkopuolelle kokonaan.
  const pushTargets = invitable
    .map(p => p.displayName)
    .filter(Boolean);
  const noEmail = withEmail.filter(x => !x.email);

  const inviteText = lang === "en"
? `Hi!

You're invited to a ${sport} Americano event

📅 ${dateStr}
📍 ${event.venue || "Venue TBA"}${event.venueAddress ? `\n   ${event.venueAddress}` : ""}
💰 Entry fee: ${panos}

Join via the Spinloop app:
👉 ${joinUrl}

Club code: ${clubCode}
Event PIN: ${event.pin}

See you on the court! 🏆
– ${clubName}`
: `Hei!

Olet kutsuttu ${sport} Americano -tapahtumaan

📅 ${dateStr}
📍 ${event.venue || "Paikka TBA"}${event.venueAddress ? `\n   ${event.venueAddress}` : ""}
💰 Osallistumismaksu: ${panos}

Kirjaudu mukaan Spinloop-sovelluksessa:
👉 ${joinUrl}

Klubikoodi: ${clubCode}
Tapahtuman PIN: ${event.pin}

Nähdään kentällä! 🏆
– ${clubName}`;

  const waText = lang === "en"
    ? encodeURIComponent(`*${sport} Americano* – ${event.venue || clubName}\n📅 ${dateStr}\n📍 ${event.venue || ""}${event.venueAddress ? `, ${event.venueAddress}` : ""}\n💰 ${panos}\n\nJoin: ${joinUrl}\nClub code: *${clubCode}*\nPIN: *${event.pin}*`)
    : encodeURIComponent(`*${sport} Americano* – ${event.venue || clubName}\n📅 ${dateStr}\n📍 ${event.venue || ""}${event.venueAddress ? `, ${event.venueAddress}` : ""}\n💰 ${panos}\n\nKirjaudu: ${joinUrl}\nKlubikoodi: *${clubCode}*\nPIN: *${event.pin}*`);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);

  function copy() {
    navigator.clipboard.writeText(inviteText);
    alert(lang === "en" ? "Copied to clipboard!" : "Kopioitu leikepöydälle!");
  }

  const sportEmoji: Record<string, string> = { pickleball: "", padel: "🏓", tennis: "🎾", squash: "🟡" };

  async function sendEmails() {
    // Pelkkä push riittää syyksi lähettää. Ennen tämä vaati sähköpostin,
    // jolloin klubissa jossa osoitteita ei ole tallessa kutsua ei voinut
    // lähettää lainkaan — eikä push olisi koskaan lähtenyt.
    if (!emails.length && !pushTargets.length) return;
    setSending(true);
    setSendResult(null);
    try {
      const functions = getFunctions(app, "us-central1");
      const sendFn = httpsCallable(functions, "sendEventInvites");
      const result: any = await sendFn({
        // playerId: palvelin tekee jokaiselle oman PIN-vapaan kutsulinkin
        // (functions/src/eventInvite.ts), jos osoite täsmää pelaajan omaan.
        recipients: withEmail.filter(x => x.email).map(x => ({ name: x.name, email: x.email, playerId: x.playerId })),
        subject: `Kutsu: ${sport} Americano — ${new Date(event.datetime).toLocaleDateString("fi-FI")}`,
        sport,
        sportEmoji: sportEmoji[event.sport] ?? "",
        // Osoite samaan riviin: sähköpostipohja näyttää yhden paikkarivin.
        venue: [event.venue, event.venueAddress].filter(Boolean).join(", "),
        dateStr,
        panos,
        pin: event.pin,
        clubCode,
        appUrl,
        // Push-kohdistus. Mukaan kaikki joilla ei ole vielä vahvistusta,
        // myös ne joilta puuttuu sähköposti — he jäivät ennen kutsun
        // ulkopuolelle kokonaan. Jo ilmoittautuneet jätetään pois: heitä
        // olisi outoa kehottaa ilmoittautumaan, ja turha ilmoitus on
        // juuri se mistä on tullut palautetta.
        clubId,
        pushNames: pushTargets,
        // Palvelin merkitsee tapahtumaan että kutsut on lähetetty, jotta
        // maanantain muistutus ei kysy jo hoidetusta tapahtumasta.
        eventId: event.id,
      });
      const pushed = result.data.pushed ?? 0;
      setSendResult(
        `✅ Lähetetty ${result.data.sent} henkilölle!` +
        (result.data.failed > 0 ? ` (${result.data.failed} epäonnistui)` : "") +
        (pushed > 0 ? ` · ${pushed} ilmoitusta puhelimeen` : "")
      );
    } catch (err: any) {
      setSendResult(`❌ Virhe: ${err.message}`);
    }
    setSending(false);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      background: "rgba(22,59,104,0.7)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      padding: "0 0 0 0",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: C.card, borderRadius: "24px 24px 0 0",
        width: "100%", maxWidth: 520, maxHeight: "85vh",
        overflowY: "auto", padding: "24px 20px 40px",
        boxShadow: "0 -8px 40px rgba(22,59,104,0.2)",
      }}>
        {/* Handle */}
        <div style={{ width: 40, height: 4, background: C.border, borderRadius: 2, margin: "0 auto 20px" }} />

        <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, fontFamily: F.heading, marginBottom: 4 }}>
          📧 {lang === "en" ? "Send invites" : "Lähetä kutsut"}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
          {event.venue} · {dateStr}
        </div>

        {/* Sähköpostilista */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            {lang === "en" ? `Recipients (${emails.length} email${emails.length !== 1 ? "s" : ""})` : `Vastaanottajat (${emails.length} sähköpostia)`}
          </div>
          {withEmail.map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, padding: "6px 10px", background: C.soft, borderRadius: 8 }}>
              <span style={{ fontSize: 16 }}>{p.email ? "✅" : "⚠️"}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{p.name}</div>
                <div style={{ fontSize: 11, color: p.email ? C.muted : C.error }}>
                  {p.email || (lang === "en" ? "No email saved" : "Ei sähköpostia tallennettu")}
                </div>
              </div>
            </div>
          ))}
          {/* Vieraat näkyvät listalla mutta eivät voi saada kutsua: ilman
              playerId:tä ei ole sähköpostia eikä laitetta. Suodatetaan
              samalla säännöllä kuin muutkin, jottei lista väitä kutsuvansa
              jo ilmoittautunutta. */}
          {participants.filter(p => !p.playerId && !p.confirmed).map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, padding: "6px 10px", background: C.soft, borderRadius: 8 }}>
              <span style={{ fontSize: 16 }}>👤</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{p.displayName}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{lang === "en" ? "Guest — no email" : "Vieras — ei sähköpostia"}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Kutsuteksti */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            {lang === "en" ? "Invite message" : "Kutsuviesti"}
          </div>
          <div style={{
            background: C.soft, borderRadius: 10, padding: "12px 14px",
            fontSize: 12, color: C.text, lineHeight: 1.7,
            whiteSpace: "pre-wrap", fontFamily: "monospace",
          }}>
            {inviteText}
          </div>
        </div>

        {/* Toimintanapit */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sendResult && (
            <div style={{
              background: sendResult.startsWith("✅") ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${sendResult.startsWith("✅") ? "#bbf7d0" : "#fecaca"}`,
              borderRadius: 10, padding: "10px 14px", fontSize: 13,
              color: sendResult.startsWith("✅") ? "#166534" : C.error, textAlign: "center",
            }}>
              {sendResult}
            </div>
          )}

          {(emails.length > 0 || pushTargets.length > 0) ? (
            <button onClick={sendEmails} disabled={sending} style={{
              ...btn, width: "100%", padding: "15px 0",
              background: sending ? C.muted : `linear-gradient(135deg, ${C.navy}, #2a5c8f)`,
              color: C.white, borderRadius: 12, fontSize: 15,
            }}>
              {sending
                ? (lang === "en" ? "Sending..." : "Lähetetään...")
                : emails.length > 0
                  ? (lang === "en"
                    ? `📧 Send invites (${emails.length} email${emails.length !== 1 ? "s" : ""}${pushTargets.length ? `, ${pushTargets.length} push` : ""})`
                    : `📧 Lähetä kutsut (${emails.length} sähköposti${emails.length !== 1 ? "a" : ""}${pushTargets.length ? `, ${pushTargets.length} ilmoitusta` : ""})`)
                  : (lang === "en"
                    ? `🔔 Send push invites (${pushTargets.length})`
                    : `🔔 Lähetä ilmoitukset (${pushTargets.length} hlö)`)}
            </button>
          ) : (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: "12px", fontSize: 13, color: C.error, textAlign: "center" }}>
              ⚠️ {lang === "en"
                ? "Everyone has confirmed — nobody to invite. Add email addresses in the Players tab to reach new players."
                : "Kaikki ovat vahvistaneet — ei ketään kutsuttavaa. Lisää pelaajille sähköpostiosoitteet Pelaajat-välilehdellä."}
            </div>
          )}

          <a href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer" style={{
            display: "block", textAlign: "center", textDecoration: "none",
            background: "#25d366", color: "#fff",
            borderRadius: 12, padding: "14px 0",
            fontSize: 15, fontWeight: 700, fontFamily: F.body,
          }}>
            💬 {lang === "en" ? "Share via WhatsApp" : "Jaa WhatsApp-viestinä"}
          </a>

          <button onClick={copy} style={{
            ...btn, width: "100%", padding: "13px 0", fontSize: 14,
            background: C.soft, color: C.navy, borderRadius: 12,
          }}>
            📋 {lang === "en" ? "Copy to clipboard" : "Kopioi teksti leikepöydälle"}
          </button>

          {/* Kalenteri */}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 2 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, textAlign: "center" }}>
              {lang === "en" ? "Add to calendar" : "Lisää kalenteriin"}
            </div>
            <CalendarButtons event={event} lang={lang} />
          </div>

          <button onClick={onClose} style={{
            ...btn, width: "100%", padding: "11px 0", fontSize: 13,
            background: "none", color: C.muted,
          }}>
            {lang === "en" ? "Close" : "Sulje"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── EventCard ─────────────────────────────────────────────────
function EventCard({ event, clubId, players, seasons = [], seasonName, onClose, onCancel, onRefresh, lang = "fi", organizer = false }: {
  event: ClubEvent; clubId: string; players: Player[];
  seasons?: Season[]; seasonName?: string;
  onClose: () => void; onCancel: () => void; onRefresh: () => void; lang?: "fi"|"en";
  organizer?: boolean;
}) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [games, setGames]               = useState<Game[]>([]);
  const [expanded, setExpanded]         = useState(false);
  // Tapahtuman tietojen muokkaus. Aiemmin naita ei voinut muuttaa
  // luomisen jalkeen lainkaan: kellonaika, paikka ja panos kirjoitettiin
  // kerran ja sen jalkeen niita vain naytettiin.
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails]   = useState(false);
  const [draftVenue, setDraftVenue]   = useState(event.venue || "");
  const [draftAddress, setDraftAddress] = useState(event.venueAddress || "");
  const [draftSeasonId, setDraftSeasonId] = useState(event.seasonId || "");
  const [draftMessages, setDraftMessages] = useState<MessagesMode>(event.messages ?? "verified");
  const [draftPanos, setDraftPanos]   = useState(String(event.panos ?? 0));
  // datetime-local haluaa muodon YYYY-MM-DDTHH:mm paikallisessa ajassa.
  const toLocalInput = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [draftDatetime, setDraftDatetime] = useState(() => toLocalInput(event.datetime));

  function openDetailsEditor() {
    setDraftVenue(event.venue || "");
    setDraftAddress(event.venueAddress || "");
    setDraftSeasonId(event.seasonId || "");
    setDraftMessages(event.messages ?? "verified");
    setDraftPanos(String(event.panos ?? 0));
    setDraftDatetime(toLocalInput(event.datetime));
    setEditingDetails(true);
  }

  async function saveDetails() {
    if (savingDetails) return;
    setSavingDetails(true);
    try {
      await updateDoc(doc(db, "clubs", clubId, "events", event.id), {
        venue: draftVenue.trim(),
        venueAddress: draftAddress.trim(),
        seasonId: draftSeasonId || deleteField(),
        messages: draftMessages,
        panos: Number(draftPanos) || 0,
        // Tallennetaan ISO-muodossa, kuten luonnissakin.
        datetime: new Date(draftDatetime).toISOString(),
      });
      setEditingDetails(false);
      onRefresh();
    } catch (err) {
      console.error("saveDetails", err);
      window.alert(lang === "fi"
        ? "Tietojen tallennus epäonnistui. Tarkista yhteys ja yritä uudelleen."
        : "Could not save the changes. Check your connection and try again.");
    }
    setSavingDetails(false);
  }

  const [addingGuest, setAddingGuest]   = useState(false);

  // Osallistujadokumentissa on vain nimi, joten kuva haetaan pelaajan
  // tunnuksella. players on jo ladattu ylempänä, joten tämä ei maksa
  // yhtään lisäkyselyä. Vieraat jäävät ilman kuvaa — heillä ei ole
  // playerId:tä eikä profiilia.
  const photoById = React.useMemo(() => {
    const m = new Map<string, string>();
    players.forEach(pl => { if (pl.photoUrl) m.set(pl.id, pl.photoUrl); });
    return m;
  }, [players]);

  // ── Round robin: kiinteät parit ────────────────────────────
  const [showPairing, setShowPairing] = useState(false);
  const [pairs, setPairs]             = useState<[string, string][]>([]);
  const [pairPick, setPairPick]       = useState<string | null>(null);

  /**
   * Pelaajan DUPR-nelinpeliluku nimen perusteella. Nimi on avain siksi,
   * että ottelut tallentavat nimiä eivätkä tunnuksia — sama valinta
   * kuin Americanossa, ja sitä ei kannata muuttaa vain tämän takia.
   */
  const ratingByName = React.useMemo(() => {
    const m = new Map<string, number>();
    players.forEach(pl => {
      const r = pl.duprDoublesRating ?? pl.ratings?.pickleball;
      if (typeof r === "number") m.set(pl.name, r);
    });
    return m;
  }, [players]);
  const [guestName, setGuestName]       = useState("");
  const [showInvite, setShowInvite]     = useState(false);
  const [showExtraSelector, setShowExtraSelector] = useState(false);
  const [extraTeam1, setExtraTeam1]               = useState<string[]>([]);
  const [extraTeam2, setExtraTeam2]               = useState<string[]>([]);

  // Osallistujat kuunnellaan aina, myös suljetussa kortissa: kortin
  // otsikkorivi näyttää ilmoittautuneet, vahvistetut ja maksaneet. Aiemmin
  // kuuntelu alkoi vasta kortin avaamisesta, joten suljettu kortti näytti
  // "Ei vielä ilmoittautumisia" ja nollat, vaikka pelaajia oli (13.10.2026).
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "clubs", clubId, "events", event.id, "participants"),
      snap => setParticipants(snap.docs.map(d => ({ id: d.id, ...d.data() } as Participant)))
    );
    return () => unsub();
  }, [clubId, event.id]);

  // Pelit vain avatussa kortissa: niitä ei näytetä otsikkorivillä.
  useEffect(() => {
    if (!expanded) return;
    const unsub = onSnapshot(
      collection(db, "clubs", clubId, "events", event.id, "games"),
      snap => setGames(snap.docs.map(d => ({ id: d.id, ...d.data() } as Game)))
    );
    return () => unsub();
  }, [expanded, clubId, event.id]);

  async function addPlayerFromList(player: Player) {
    const ref = doc(db, "clubs", clubId, "events", event.id, "participants", player.id);
    await setDoc(ref, {
      playerId: player.id, displayName: player.name,
      confirmed: false, paid: false,
      createdAt: new Date().toISOString(),
    }, { merge: true });
  }

  async function addGuest() {
    if (!guestName.trim()) return;
    const ref = doc(collection(db, "clubs", clubId, "events", event.id, "participants"));
    await setDoc(ref, {
      guestName: guestName.trim(), displayName: guestName.trim(),
      confirmed: true, paid: false,
      createdAt: new Date().toISOString(),
    });
    setGuestName(""); setAddingGuest(false);
  }

  async function togglePaid(p: Participant) {
    await updateDoc(
      doc(db, "clubs", clubId, "events", event.id, "participants", p.id),
      { paid: !p.paid }
    );
  }

  // Paikalla-kirjaus kentän laidalla. `confirmed` kertoo vain
  // ilmoittautumisesta, joten ilman tätä tulematta jäänyt sai liigan
  // läsnäolopisteen. Pelanneet merkitään paikalle itsestään illan
  // päättyessä (nightResults), joten tätä tarvitaan lähinnä niille, jotka
  // tulivat mutta eivät ehtineet kentälle.
  async function toggleAttended(p: Participant) {
    await updateDoc(
      doc(db, "clubs", clubId, "events", event.id, "participants", p.id),
      { attended: !p.attended }
    );
  }

  async function removeParticipant(p: Participant) {
    await deleteDoc(doc(db, "clubs", clubId, "events", event.id, "participants", p.id));
  }

  /**
   * Ehdottaa parit DUPR-luvun perusteella niille, joita ei ole vielä
   * pariutettu. Vahvin ja heikoin yhteen, toiseksi vahvin ja toiseksi
   * heikoin, ja niin edelleen — näin parit ovat keskenään tasavahvoja
   * eikä yksi pari kävele muiden yli.
   *
   * Käsin tehdyt parit säilyvät: tämä on ehdotus, ei ylikirjoitus.
   * Ilman lukua olevat menevät loppuun satunnaisessa järjestyksessä,
   * joten toiminto toimii myös silloin kun DUPR on alhaalla tai kukaan
   * ei ole yhdistänyt tiliään.
   */
  function suggestPairs() {
    const taken = new Set(pairs.flat());
    const free = participants
      .filter(p => p.confirmed && !taken.has(p.displayName))
      .map(p => p.displayName);

    const rated = free.filter(n => ratingByName.has(n))
      .sort((a, b) => (ratingByName.get(b) as number) - (ratingByName.get(a) as number));
    const unrated = free.filter(n => !ratingByName.has(n))
      .sort(() => Math.random() - 0.5);
    const ordered = [...rated, ...unrated];

    const added: [string, string][] = [];
    let lo = 0;
    let hi = ordered.length - 1;
    while (lo < hi) {
      added.push([ordered[lo], ordered[hi]]);
      lo++;
      hi--;
    }
    setPairs([...pairs, ...added]);
    setPairPick(null);
  }

  /** Napautus pariutuksessa: ensimmäinen valitsee, toinen muodostaa parin. */
  function tapForPair(name: string) {
    if (pairPick === null) { setPairPick(name); return; }
    if (pairPick === name) { setPairPick(null); return; }
    setPairs([...pairs, [pairPick, name]]);
    setPairPick(null);
  }

  /**
   * Luo round robin -ottelut: jokainen pari kohtaa jokaisen muun kerran.
   *
   * Kiertomenetelmä on sama kuin Americanossa, mutta yksikkönä on pari
   * eikä pelaaja. Pariton määrä pareja saa BYE-täytteen, jolloin yksi
   * pari lepää kierroksen.
   *
   * Otteluita EI rajata kenttien määrällä, toisin kuin Americanossa.
   * Round robinin koko idea on että jokainen kohtaa jokaisen; ottelun
   * pudottaminen rikkoisi sen hiljaa. Kierrosnumero on siis looginen
   * ryhmittely — jos kenttiä on vähemmän, kierros vain kestää kauemmin.
   */
  async function generateRoundRobinGames() {
    if (pairs.length < 2) {
      alert(lang === "fi" ? "Vähintään kaksi paria tarvitaan." : "At least two pairs are required.");
      return;
    }

    // Muodon vaihto kesken tapahtuman on sallittua mutta ei vahingossa:
    // olemassa olevat ottelut korvataan, ja niihin kirjatut tulokset
    // katoavat mukana.
    if (games.length > 0) {
      const ok = window.confirm(lang === "fi"
        ? `Tapahtumassa on ${games.length} ottelua. Ne poistetaan ja korvataan round robin -otteluilla. Kirjatut tulokset katoavat. Jatketaanko?`
        : `This event has ${games.length} matches. They will be deleted and replaced with round robin matches. Recorded scores will be lost. Continue?`);
      if (!ok) return;
      await Promise.all(games.map(g =>
        deleteDoc(doc(db, "clubs", clubId, "events", event.id, "games", g.id))));
    }

    const ts: ([string, string] | null)[] = pairs.length % 2 === 0 ? [...pairs] : [...pairs, null];
    const M = ts.length;
    const gamesList: Omit<Game, "id">[] = [];

    for (let round = 0; round < M - 1; round++) {
      const arranged: ([string, string] | null)[] = [ts[0]];
      for (let i = 1; i < M; i++) {
        arranged.push(ts[1 + ((i - 1 + round) % (M - 1))]);
      }
      for (let k = 0; k < M / 2; k++) {
        const a = arranged[k];
        const b = arranged[M - 1 - k];
        if (a && b) gamesList.push({ team1: [a[0], a[1]], team2: [b[0], b[1]], round: round + 1 });
      }
    }

    await Promise.all(gamesList.map((g, idx) => {
      const ref = doc(collection(db, "clubs", clubId, "events", event.id, "games"));
      return setDoc(ref, { ...g, order: idx, createdAt: new Date().toISOString() });
    }));
    await updateDoc(doc(db, "clubs", clubId, "events", event.id), { gameType: "RoundRobin" });

    setShowPairing(false);
    alert(lang === "fi"
      ? `Luotiin ${gamesList.length} ottelua, ${M - 1} kierrosta.`
      : `Created ${gamesList.length} matches over ${M - 1} rounds.`);
    onRefresh();
  }

  async function generateAmericanoGames() {
    const confirmed = participants.filter(p => p.confirmed);
    if (confirmed.length < 4) {
      alert(lang === "fi" ? "Vähintään 4 vahvistettua pelaajaa tarvitaan." : "At least 4 confirmed players required.");
      return;
    }

    // Lista kierretään tapahtumakohtaisesti: ympyrämenetelmä pitää
    // ensimmäisen pelaajan paikallaan, ja neljän pelaajan illassa juuri
    // hän on se joka pelaa kaikki kolme kierrosta samassa päässä.
    // Ilman kiertoa se olisi joka ilta sama ihminen.
    const names = rotateForEvent(confirmed.map(p => p.displayName), event.id);

    // Arvonta on src/utils/americano.ts:ssa, jotta "Lisää kierros"
    // tuottaa täsmälleen samanmuotoisia otteluita kuin tämä.
    const raw: AmericanoGame[] = [];
    const roundOf: number[] = [];
    for (let round = 0; round < americanoRoundCount(names.length); round++) {
      americanoRound(names, round).forEach(g => {
        raw.push(g);
        roundOf.push(round + 1);
      });
    }

    // Puolet tasataan koko kierrolle kerralla: ympyrämenetelmä pitää
    // ensimmäisen pelaajan paikallaan, joten ilman tätä hän pelaisi
    // jokaisen ottelunsa samassa päässä. Pareihin tasaus ei koske.
    const gamesList: Omit<Game, "id">[] = balanceSides(raw, games).map((g, i) => ({
      team1: g.team1, team2: g.team2, round: roundOf[i],
    }));

    await Promise.all(gamesList.map((g, idx) => {
      const ref = doc(collection(db, "clubs", clubId, "events", event.id, "games"));
      return setDoc(ref, { ...g, order: idx, createdAt: new Date().toISOString() });
    }));

    const totalRounds = gamesList.length > 0 ? Math.max(...gamesList.map(g => g.round ?? 0)) : 0;
    alert(lang === "fi"
      ? `${gamesList.length} ottelua / ${totalRounds} kierrosta generoitu!`
      : `${gamesList.length} matches / ${totalRounds} rounds generated!`);
  }

  /**
   * Liittää yhden uuden kierroksen olemassa olevien perään.
   *
   * 11 pisteen peleissä ympyräkierto loppuu kesken illan: se tuottaa
   * `pelaajat − 1` kierrosta, eli neljällä pelaajalla kolme ottelua.
   * Ennen tätä ainoa jatko oli *Lisää ottelu*, joka vaatii joukkueiden
   * valitsemisen käsin — kentän laidalla hidasta juuri silloin kun
   * pitäisi vain jatkaa.
   *
   * Otteluita ei generoida uudelleen vaan liitetään: regenerointi
   * poistaisi kirjatut tulokset mukanaan. Kierto jatkuu siitä mihin
   * viimeinen kierros jäi ja alkaa kierron loputtua alusta, joten
   * pariutukset toistuvat samassa järjestyksessä.
   *
   * Pelaajalista luetaan nyt, ei generointihetkellä: myöhässä tullut
   * pääsee mukaan seuraavaan kierrokseen ilman erillistä toimenpidettä.
   */
  async function addAmericanoRound() {
    // Sama kierto kuin generoinnissa, samalla siemenellä: liitetty
    // kierros jatkaa samaa kiertoa eikä aloita pariutuksia alusta.
    const names = rotateForEvent(
      participants.filter(p => p.confirmed).map(p => p.displayName),
      event.id
    );
    if (names.length < 4) {
      alert(lang === "fi"
        ? "Vähintään 4 vahvistettua pelaajaa tarvitaan."
        : "At least 4 confirmed players required.");
      return;
    }

    const maxRound = games.reduce((max, g) => Math.max(max, g.round ?? 0), 0);
    const nextRound = maxRound + 1;
    // Tasaus katsoo illan jo pelatut ottelut: lisätty kierros korjaa
    // siihenastista vinoumaa eikä aloita puolijakoa nollasta.
    const fresh = balanceSides(americanoRound(names, maxRound), games);
    if (fresh.length === 0) return;

    await Promise.all(fresh.map((g, idx) => {
      const ref = doc(collection(db, "clubs", clubId, "events", event.id, "games"));
      return setDoc(ref, {
        team1: g.team1, team2: g.team2,
        round: nextRound,
        order: games.length + idx,
        createdAt: new Date().toISOString(),
      });
    }));

    const resting = names.length - fresh.length * 4;
    alert(lang === "fi"
      ? `Kierros ${nextRound}: ${fresh.length} ottelua.` +
        (resting > 0 ? ` ${resting} pelaajaa lepää.` : "")
      : `Round ${nextRound}: ${fresh.length} matches.` +
        (resting > 0 ? ` ${resting} player(s) resting.` : ""));
    onRefresh();
  }

  function openExtraSelector() {
    const confirmed = participants.filter(p => p.confirmed).map(p => p.displayName);
    setExtraTeam1([]);
    setExtraTeam2([]);
    setShowExtraSelector(true);
  }

  function toggleExtraPlayer(name: string) {
    // Kierrätä: ei valittu → tiimi1 → tiimi2 → ei valittu
    if (extraTeam1.includes(name)) {
      setExtraTeam1(t => t.filter(n => n !== name));
      setExtraTeam2(t => [...t, name]);
    } else if (extraTeam2.includes(name)) {
      setExtraTeam2(t => t.filter(n => n !== name));
    } else {
      if (extraTeam1.length <= extraTeam2.length) {
        setExtraTeam1(t => [...t, name]);
      } else {
        setExtraTeam2(t => [...t, name]);
      }
    }
  }

  async function addExtraGame() {
    if (extraTeam1.length < 1 || extraTeam2.length < 1) return;
    const maxRound = games.reduce((max, g) => Math.max(max, g.round ?? 0), 0);
    const ref = doc(collection(db, "clubs", clubId, "events", event.id, "games"));
    await setDoc(ref, {
      team1: extraTeam1, team2: extraTeam2,
      round: maxRound + 1, isExtra: true,
      order: games.length, createdAt: new Date().toISOString(),
    });
    setExtraTeam1([]);
    setExtraTeam2([]);
    setShowExtraSelector(false);
  }

  const confirmedCount = participants.filter(p => p.confirmed).length;
  const paidCount      = participants.filter(p => p.paid).length;
  const attendedCount  = participants.filter(p => p.attended).length;

  return (
    <div style={{ ...card, borderLeft: seasonName ? "5px solid var(--sl-success)" : undefined }}>
      {/* Otsikko — mockupin mukainen: iso nimi, liigarivi vihreänä, PIN-laatta, tilapilleri */}
      <div onClick={() => setExpanded(!expanded)} style={{ cursor: "pointer" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 19, lineHeight: 1.05, letterSpacing: "0.02em", color: C.text }}>
              {event.venue || (lang === "fi" ? "Tapahtuma" : "Event")}
            </div>
            {seasonName && (
              <div style={{ fontSize: 11, fontWeight: 700, color: C.accent }}>🏆 {seasonName}</div>
            )}
            {event.venueAddress && (
              <div style={{ fontSize: 11, color: C.muted }}>{event.venueAddress}</div>
            )}
            <div style={{ fontSize: 12, color: C.text, fontWeight: 600, marginTop: 2 }}>
              {new Date(event.datetime).toLocaleString("fi-FI", { weekday:"short", day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}
            </div>
            {/* Tila yhdellä silmäyksellä: ketkä ovat tulossa ja onko kutsut lähetetty */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 6, alignSelf: "flex-start", fontSize: 10, fontWeight: 700, padding: "3px 9px 3px 7px", borderRadius: 999,
              color: confirmedCount > 0 ? C.accent : C.muted, background: confirmedCount > 0 ? "var(--sl-success-a12)" : C.soft }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: confirmedCount > 0 ? C.accent : C.muted, boxShadow: confirmedCount > 0 ? "0 0 0 3px var(--sl-success-a12)" : undefined }} />
              {event.invitesSentAt
                ? (lang === "fi" ? `Kutsut lähetetty · ${confirmedCount} / ${participants.length} vahvistanut` : `Invites sent · ${confirmedCount} / ${participants.length} confirmed`) +
                  (event.playersReminded ? (lang === "fi" ? ` · muistutettu ${event.playersReminded}` : ` · ${event.playersReminded} reminded`) : "")
                : participants.length > 0
                  ? (lang === "fi" ? `${confirmedCount} / ${participants.length} vahvistanut` : `${confirmedCount} / ${participants.length} confirmed`)
                  : (lang === "fi" ? "Ei vielä ilmoittautumisia" : "No sign-ups yet")}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
            <div style={{
              fontFamily: F.heading, fontWeight: 700, fontSize: 24, letterSpacing: "0.2em", lineHeight: 1,
              padding: "6px 10px 6px 12px", borderRadius: 10, background: C.navy, color: C.white,
            }}>
              {event.pin}
            </div>
            <div style={{ fontSize: 9, letterSpacing: "0.1em", color: C.muted, marginTop: 3 }}>PIN</div>
            {!organizer && (
              <button onClick={e => { e.stopPropagation(); openDetailsEditor(); }}
                style={{ ...btn, background: C.soft, color: C.navy, fontSize: 11, padding: "6px 10px", marginTop: 6 }}>
                ✏️ {lang === "fi" ? "Muokkaa" : "Edit"}
              </button>
            )}
          </div>
        </div>
        {/* Tapahtuman tietojen muokkaus.
            Lomake oli kadonnut: openDetailsEditor asetti editingDetails
            todeksi ja saveDetails oli valmiina, mutta kumpaakaan ei luettu
            eika kutsuttu mistaan — Muokkaa-nappi ei siis tehnyt mitaan.
            eslint kertoi tasta "assigned a value but never used"
            -varoituksina. */}
        {editingDetails && (
          <div onClick={e => e.stopPropagation()} style={{
            marginTop: 12, padding: 12, borderRadius: 10,
            background: C.soft, border: `1px solid ${C.border}`,
            display: "grid", gap: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {lang === "fi" ? "Muokkaa tapahtumaa" : "Edit event"}
            </div>

            <label style={{ fontSize: 11, color: C.muted }}>
              {lang === "fi" ? "Aika" : "Time"}
              <input type="datetime-local" value={draftDatetime}
                onChange={e => setDraftDatetime(e.target.value)}
                style={{ ...input, marginTop: 3 }} />
            </label>

            <label style={{ fontSize: 11, color: C.muted }}>
              {lang === "fi" ? "Paikka" : "Venue"}
              <input value={draftVenue} onChange={e => setDraftVenue(e.target.value)}
                placeholder={lang === "fi" ? "Esim. Vuoksenniskan koulu" : "e.g. Main hall"}
                style={{ ...input, marginTop: 3 }} />
            </label>

            <label style={{ fontSize: 11, color: C.muted }}>
              {lang === "fi" ? "Osoite" : "Address"}
              <input value={draftAddress} onChange={e => setDraftAddress(e.target.value)}
                style={{ ...input, marginTop: 3 }} />
            </label>

            <label style={{ fontSize: 11, color: C.muted }}>
              {lang === "fi" ? "Panos (€)" : "Stake (€)"}
              <input type="number" inputMode="decimal" value={draftPanos}
                onChange={e => setDraftPanos(e.target.value)}
                style={{ ...input, marginTop: 3 }} />
            </label>

            {seasons.length > 0 && (
              <label style={{ fontSize: 11, color: C.muted }}>
                {lang === "fi" ? "Kausi" : "Season"}
                <select value={draftSeasonId} onChange={e => setDraftSeasonId(e.target.value)}
                  style={{ ...input, marginTop: 3 }}>
                  <option value="">{lang === "fi" ? "— ei kautta —" : "— no season —"}</option>
                  {seasons.map(s2 => <option key={s2.id} value={s2.id}>{s2.name}</option>)}
                </select>
              </label>
            )}

            <MessagesModeSelect value={draftMessages} onChange={setDraftMessages} lang={lang} style={input} />

            <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
              <button onClick={saveDetails} disabled={savingDetails}
                style={{ ...btn, flex: 1, background: C.navy, color: C.white, opacity: savingDetails ? 0.6 : 1 }}>
                {savingDetails
                  ? (lang === "fi" ? "Tallennetaan…" : "Saving…")
                  : (lang === "fi" ? "Tallenna" : "Save")}
              </button>
              <button onClick={() => setEditingDetails(false)} disabled={savingDetails}
                style={{ ...btn, flex: 1, background: C.soft, color: C.muted }}>
                {lang === "fi" ? "Peruuta" : "Cancel"}
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, marginTop: 10 }}>
          {[
            { label: lang === "fi" ? "Pelaajaa" : "Players", value: participants.length, color: C.text },
            { label: lang === "fi" ? "Vahvistettu" : "Confirmed", value: confirmedCount, color: confirmedCount > 0 ? C.accent : C.text },
            { label: lang === "fi" ? "Paikalla" : "Present", value: attendedCount, color: C.text },
            { label: lang === "fi" ? "Maksettu" : "Paid", value: paidCount, color: C.text },
          ].map(s => (
            <div key={s.label} style={{ background: C.soft, borderRadius: 10, padding: "8px 4px", textAlign: "center" }}>
              <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 20, lineHeight: 1, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {!expanded && (
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 8 }}>
            {lang === "fi" ? "Napauta avataksesi osallistujat ja ottelut ▾" : "Tap to open participants and matches ▾"}
          </div>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>

          {/* Osallistujat */}
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            {lang === "en" ? "Participants" : "Osallistujat"}
          </div>
          {participants.some(p => p.confirmed) && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: -4, marginBottom: 10, lineHeight: 1.4 }}>
              {lang === "en"
                ? "Tap Present? for everyone who came. Players with a result are marked present automatically when the event ends."
                : "Napauta Paikalla? jokaiselle joka tuli. Tuloksen saaneet merkitään paikalle itsestään, kun tapahtuma päätetään."}
            </div>
          )}

          {participants.map(p => (
            <div key={p.id} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 10px", background: C.soft, borderRadius: 8, marginBottom: 6,
            }}>
              <Avatar
                name={p.displayName} photoUrl={photoById.get(p.playerId || "")}
                size={28} fontSize={12}
                background={p.confirmed ? C.navy : C.muted} color={C.white}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{p.displayName}</div>
                <div style={{ fontSize: 10, color: C.muted }}>
                  {p.confirmed
                    ? (lang === "fi" ? "✓ Vahvistettu" : "✓ Confirmed")
                    : p.source === "player"
                      ? (lang === "fi" ? "✕ Ei pääse" : "✕ Can't make it")
                      : (lang === "fi" ? "Kutsuttu, ei vastannut" : "Invited, no answer")} {p.guestName ? (lang === "fi" ? "· Vieras" : "· Guest") : ""}
                </div>
              </div>
              {/* source: "admin" estaa ilmoituksen omasta klikkauksesta:
                  pelaajan itse luomalla rivilla lukee "player", ja
                  sendParticipantPush laukeaisi siita yllapitajalle. */}
              {!p.confirmed && (
                <button onClick={() => updateDoc(doc(db, "clubs", clubId, "events", event.id, "participants", p.id), { confirmed: true, source: "admin" })}
                  style={{ ...btn, padding: "4px 10px", fontSize: 11, background: "#fef9c3", color: "#92400e", border: "1px solid #fde68a" }}>
                  {lang === "en" ? "Confirm" : "Vahvista"}
                </button>
              )}
              {p.confirmed && (
                <button onClick={() => toggleAttended(p)} style={{
                  ...btn, padding: "4px 10px", fontSize: 11,
                  background: p.attended ? "var(--sl-primary-a06)" : C.soft,
                  color: p.attended ? C.navy : C.muted,
                  border: `1px solid ${p.attended ? "var(--sl-primary-a20)" : C.border}`,
                }}>
                  {p.attended ? (lang === "fi" ? "✓ Paikalla" : "✓ Present") : (lang === "fi" ? "Paikalla?" : "Present?")}
                </button>
              )}
              <button onClick={() => togglePaid(p)} style={{
                ...btn, padding: "4px 10px", fontSize: 11,
                background: p.paid ? "#d1fae5" : C.soft,
                color: p.paid ? "#059669" : C.muted,
                border: `1px solid ${p.paid ? "#a7f3d0" : C.border}`,
              }}>
                {p.paid ? (lang === "fi" ? "Maksettu" : "Paid") : (lang === "fi" ? "Maksu?" : "Pay?")}
              </button>
              {!organizer && (
                <button onClick={() => removeParticipant(p)} style={{ ...btn, padding: "4px 8px", background: "none", color: C.error, fontSize: 16 }}>
                  ×
                </button>
              )}
            </div>
          ))}

          {/* Lisää pelaaja listalta */}
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{lang === "en" ? "Add from club players:" : "Lisää klubin pelaajista:"}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {players
                .filter(pl => !participants.some(p => p.playerId === pl.id))
                .map(pl => (
                  <button key={pl.id} onClick={() => addPlayerFromList(pl)} style={{
                    ...btn, padding: "6px 12px", fontSize: 12,
                    background: C.soft, color: C.text, border: `1px solid ${C.border}`,
                  }}>
                    + {pl.name}
                  </button>
                ))
              }
            </div>
          </div>

          {/* Lisää vieras */}
          {addingGuest ? (
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <input value={guestName} onChange={e => setGuestName(e.target.value)}
                placeholder={lang === "en" ? "Guest name" : "Vieraan nimi"} style={{ ...input, marginBottom: 0, flex: 1 }} autoFocus />
              <button onClick={addGuest} style={{ ...btn, background: C.navy, color: C.white }}>+</button>
              <button onClick={() => setAddingGuest(false)} style={{ ...btn, background: C.soft, color: C.text }}>×</button>
            </div>
          ) : (
            <button onClick={() => setAddingGuest(true)} style={{ ...btn, marginTop: 8, background: C.soft, color: C.muted, fontSize: 12, width: "100%" }}>
              {lang === "en" ? "+ Add guest" : "+ Lisää vieras"}
            </button>
          )}

          {/* Toiminnot */}
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <button onClick={() => setShowInvite(true)} style={{ ...btn, flex: 1, background: `linear-gradient(135deg, ${C.navy}, #2a5c8f)`, color: C.white, fontSize: 13 }}>
              📧 {lang === "en" ? "Send invites" : "Lähetä kutsut"}
            </button>
            {confirmedCount >= 4 && games.length === 0 && (
              <button onClick={generateAmericanoGames} style={{ ...btn, flex: 1, background: C.gold, color: C.navy }}>
                <PickleballBall /> {lang === "en" ? "Americano" : "Americano"}
              </button>
            )}
            {confirmedCount >= 4 && (
              <button onClick={() => setShowPairing(v => !v)}
                style={{ ...btn, flex: 1, background: showPairing ? C.navy : `var(--sl-primary-a10)`, color: showPairing ? C.white : C.navy }}>
                🔁 {lang === "en" ? "Round robin" : "Round robin"}
              </button>
            )}
            {games.length > 0 && (
              <>
                {confirmedCount >= 4 && event.gameType !== "RoundRobin" && (
                  <button onClick={addAmericanoRound} style={{ ...btn, flex: 1, background: C.gold, color: C.navy, fontSize: 12 }}>
                    <PickleballBall size={14} /> {lang === "en" ? "Add round" : "Lisää kierros"}
                  </button>
                )}
                {/* Lisää yhden ottelun käsin valituilla joukkueilla. Nappi
                    luki "Extra erä" / "Extra round", mikä lupasi kierroksen
                    vaikka se lisää yhden ottelun — kierroksen lisää yllä
                    oleva nappi. */}
                <button onClick={openExtraSelector} style={{ ...btn, flex: 1, background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", fontSize: 12 }}>
                  ➕ {lang === "en" ? "Add match" : "Lisää ottelu"}
                </button>
                <button onClick={async () => {
                  if (!window.confirm(lang === "en" ? "Delete all matches and regenerate?" : "Poistetaanko kaikki ottelut ja generoidaan uudet?")) return;
                  await Promise.all(games.map(g => deleteDoc(doc(db, "clubs", clubId, "events", event.id, "games", g.id))));
                  // Round robin ei regeneroidu itsestään: parit ovat
                  // ylläpitäjän valinta eikä niitä voi arvata otteluista.
                  // Ilman tätä haaraa nappi muuttaisi muodon Americanoksi
                  // huomaamatta.
                  if (event.gameType === "RoundRobin") { setPairs([]); setPairPick(null); setShowPairing(true); return; }
                  await generateAmericanoGames();
                }} style={{ ...btn, flex: 1, background: C.soft, color: C.muted, fontSize: 12 }}>
                  ↺ {lang === "en" ? "Regenerate" : "Regeneroi"}
                </button>
              </>
            )}
            <button onClick={() => {
              if (!window.confirm(closeEventConfirmText(games, lang, !!event.seasonId, participants))) return;
              onClose();
            }} style={{ ...btn, flex: 1, background: "#fef2f2", color: C.error, border: `1px solid #fecaca` }}>
              {lang === "en" ? "End event" : "Päätä tapahtuma"}
            </button>
            {!organizer && (
              <button onClick={onCancel} style={{ ...btn, flex: 1, background: C.soft, color: C.muted, fontSize: 12 }}>
                ✕ {lang === "en" ? "Cancel event" : "Peruuta tapahtuma"}
              </button>
            )}
          </div>

          {/* Viestiketju (docs/ideat.md G1). Järjestäjä kirjoittaa
              pelaajanäkymässä omalla nimellään, joten tässä vain admin. */}
          {!organizer && (
            <EventMessages clubId={clubId} eventId={event.id} role="club_admin" mode={event.messages}
              clubName={localStorage.getItem("spinloop_club_name") || ""} lang={lang} />
          )}

          {/* Round robin — parinmuodostus */}
          {showPairing && (() => {
            const taken = new Set(pairs.flat());
            const free = participants.filter(p => p.confirmed && !taken.has(p.displayName));
            return (
              <div style={{
                background: `var(--sl-primary-a06)`, border: `1px solid var(--sl-primary-a20)`,
                borderRadius: 12, padding: "14px 16px", marginTop: 12,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>
                  🔁 {lang === "en" ? "Fixed pairs" : "Kiinteät parit"}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
                  {lang === "en"
                    ? "Tap two players to pair them. Every pair plays every other pair once."
                    : "Napauta kahta pelaajaa, niin heistä tulee pari. Jokainen pari pelaa jokaista muuta paria vastaan kerran."}
                </div>

                {/* Pariuttamattomat */}
                {free.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                    {free.map(p => {
                      const sel = pairPick === p.displayName;
                      const r = ratingByName.get(p.displayName);
                      return (
                        <button key={p.id} onClick={() => tapForPair(p.displayName)}
                          style={{
                            padding: "7px 12px", borderRadius: 20, cursor: "pointer",
                            border: `1px solid ${sel ? C.navy : C.border}`,
                            background: sel ? C.navy : C.white,
                            color: sel ? C.white : C.text,
                            fontSize: 12, fontWeight: 600, fontFamily: F.body,
                          }}>
                          {p.displayName}
                          {r != null && (
                            <span style={{ opacity: 0.6, marginLeft: 5, fontSize: 10 }}>{r.toFixed(2)}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Muodostetut parit */}
                {pairs.map((pr, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 10px", background: C.card,
                    border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 6,
                  }}>
                    <span style={{ fontSize: 11, color: C.muted, width: 18 }}>{i + 1}.</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: C.text }}>
                      {pr[0]} + {pr[1]}
                    </span>
                    <button onClick={() => setPairs(pairs.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: C.error, fontSize: 14 }}>
                      ✕
                    </button>
                  </div>
                ))}

                {free.length === 1 && (
                  <div style={{ fontSize: 11, color: "#b45309", marginTop: 8 }}>
                    {lang === "en"
                      ? "One player is left without a pair. Add one more or leave them out."
                      : "Yksi pelaaja jää ilman paria. Lisää yksi tai jätä hänet pois."}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <button onClick={suggestPairs} disabled={free.length < 2}
                    style={{ ...btn, flex: 1, background: C.soft, color: C.navy, fontSize: 12, opacity: free.length < 2 ? 0.5 : 1 }}>
                    ✨ {lang === "en" ? "Suggest by rating" : "Ehdota DUPR:n mukaan"}
                  </button>
                  <button onClick={() => { setPairs([]); setPairPick(null); }} disabled={pairs.length === 0}
                    style={{ ...btn, flex: 1, background: C.soft, color: C.muted, fontSize: 12, opacity: pairs.length === 0 ? 0.5 : 1 }}>
                    ↺ {lang === "en" ? "Clear" : "Tyhjennä"}
                  </button>
                  <button onClick={generateRoundRobinGames} disabled={pairs.length < 2}
                    style={{ ...btn, flex: "1 1 100%", background: C.gold, color: C.navy, opacity: pairs.length < 2 ? 0.5 : 1 }}>
                    <PickleballBall /> {lang === "en" ? `Generate (${pairs.length} pairs)` : `Luo ottelut (${pairs.length} paria)`}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Extra erä — joukkuerakennin */}
          {showExtraSelector && (
            <div style={{
              background: "#f0fdf4", border: "1px solid #bbf7d0",
              borderRadius: 12, padding: "14px 16px", marginTop: 12,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a", marginBottom: 4 }}>
                ➕ {lang === "en" ? "Build teams" : "Rakenna joukkueet"}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>
                {lang === "en"
                  ? "Tap a player → goes to Team 1 → tap again → Team 2 → third tap removes"
                  : "Napauta pelaajaa → sijoittuu Tiimi 1:een → uusi napautus → Tiimi 2 → kolmas poistaa"}
              </div>

              {/* Pelaajachipsit */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {participants.filter(p => p.confirmed).map(p => {
                  const inT1 = extraTeam1.includes(p.displayName);
                  const inT2 = extraTeam2.includes(p.displayName);
                  return (
                    <button
                      key={p.id}
                      onClick={() => toggleExtraPlayer(p.displayName)}
                      style={{
                        ...btn,
                        padding: "7px 14px", fontSize: 13, fontWeight: 700,
                        background: inT1 ? C.navy : inT2 ? C.gold : "#e5e7eb",
                        color: inT1 ? "#fff" : inT2 ? "#0f2744" : "#6b7280",
                        border: "none", borderRadius: 20,
                        transition: "all 0.15s",
                      }}
                    >
                      {inT1 ? "①" : inT2 ? "②" : "○"} {p.displayName}
                    </button>
                  );
                })}
              </div>

              {/* Joukkuenäkymä */}
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <div style={{ flex: 1, background: "#dbeafe", borderRadius: 8, padding: "8px 10px", minHeight: 44 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#1d4ed8", marginBottom: 4 }}>TIIMI 1</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--sl-primary)" }}>
                    {extraTeam1.length > 0 ? extraTeam1.join(" & ") : <span style={{ color: "#93c5fd" }}>—</span>}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", fontWeight: 700, color: C.muted, fontSize: 13 }}>VS</div>
                <div style={{ flex: 1, background: "#fef9c3", borderRadius: 8, padding: "8px 10px", minHeight: 44 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#a16207", marginBottom: 4 }}>TIIMI 2</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--sl-primary)" }}>
                    {extraTeam2.length > 0 ? extraTeam2.join(" & ") : <span style={{ color: "#fde047" }}>—</span>}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={addExtraGame}
                  disabled={extraTeam1.length === 0 || extraTeam2.length === 0}
                  style={{ ...btn, flex: 1, fontSize: 13, color: "#fff",
                    background: extraTeam1.length > 0 && extraTeam2.length > 0 ? "#16a34a" : "#d1d5db" }}
                >
                  {lang === "en" ? "✓ Create match" : "✓ Luo ottelu"}
                </button>
                <button
                  onClick={() => setShowExtraSelector(false)}
                  style={{ ...btn, background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb", fontSize: 13 }}
                >
                  {lang === "en" ? "Cancel" : "Peruuta"}
                </button>
              </div>
            </div>
          )}

          {/* Kutsumodaali */}
          {showInvite && (
            <InviteModal
              event={event}
              participants={participants}
              players={players}
              clubId={clubId}
              onClose={() => setShowInvite(false)}
              lang={lang}
            />
          )}

          {/* Ottelut + Scoreboard */}
          {games.length > 0 && (
            <>
              <GamesSection clubId={clubId} eventId={event.id} games={games} lang={lang} participants={participants} venue={event.venue} seasonId={event.seasonId} />
              <Scoreboard games={games} participants={participants} lang={lang} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Ottelut ───────────────────────────────────────────────────
function GamesSection({ clubId, eventId, games, lang = "fi", participants = [], finished = false, venue, seasonId }: {
  clubId: string; eventId: string; games: Game[]; lang?: "fi"|"en"; venue?: string;
  /** Pelaajien korjausta varten (MatchPlayersEditor). */
  participants?: Participant[];
  finished?: boolean;
  /**
   * Kauden tunnus, jos ilta kuuluu liigaan. Kesken jääneestä kierrosta
   * varoitetaan kaikissa illoissa, mutta vain liigaillassa sillä on
   * seuraus kausipisteisiin (utils/league.ts).
   */
  seasonId?: string;
}) {
  const [scores, setScores]       = useState<Record<string, { s1: string; s2: string }>>({});
  const [editingTeams, setEditingTeams] = useState<string | null>(null);
  const names = participants.map(p => p.displayName).filter(Boolean);
  // Puolten nimet näytetään vain jos pelipaikalle on annettu ne. Klubi,
  // jonka kentät ovat samanarvoisia, ei näe tästä mitään.
  const sides = sideNames(useClubVenues(clubId), venue);
  // Neljän pelaajan kolme kierrosta ei ole tasattavissa: kaikki kahdeksan
  // mahdollista puolijakoa jättävät jonkun kolmesti samaan päähän. Silloin
  // on rehellisempää kertoa se ja neuvoa kentällä tehtävä korjaus kuin
  // antaa pelaajan päätellä että arvonta on rikki.
  const sidesUneven = !!sides && worstSideBias(games) > 1;
  // Täydet kierrot: klubin päätös 2.11.2026. Kerrotaan illan aikana,
  // ettei vajaita iltoja tarvitse ratkoa jälkikäteen.
  const vajaa = roundsToCompleteCycle(games);

  /**
   * Kirjoitusvirhe luettavaksi tekstiksi.
   *
   * Ilman tätä epäonnistunut tallennus ei näkynyt mitenkään: nappi ei
   * vaihtanut väriä eikä mitään tapahtunut. Pelaaja ilmoitti 1.11.2026
   * ettei "pystynyt laittamaan tuloksia", ja oire oli juuri tämä —
   * `updateDoc` hylkäsi lupauksen eikä sitä otettu kiinni missään.
   *
   * @param e Firestoren virhe.
   * @return suomenkielinen selitys ja ohje.
   */
  function kirjoitusVirhe(e: unknown): string {
    const code = (e as { code?: string })?.code ?? "";
    if (code.includes("permission-denied")) {
      return lang === "fi"
        ? "Sinulla ei ole oikeutta kirjata tuloksia. Oikeudet myöntää klubin ylläpitäjä — ja ne alkavat toimia vasta kun kirjaudut sisään kutsulinkillä, ei tapahtuman PIN-koodilla."
        : "You do not have permission to record results. A club admin grants it — and it only takes effect when you sign in with an invite link, not with the event PIN.";
    }
    if (code.includes("unavailable") || code.includes("network")) {
      return lang === "fi"
        ? "Tulos ei tallentunut: yhteys katkesi. Yritä uudelleen."
        : "The result was not saved: the connection dropped. Try again.";
    }
    return lang === "fi"
      ? `Tulos ei tallentunut${code ? ` (${code})` : ""}. Yritä uudelleen.`
      : `The result was not saved${code ? ` (${code})` : ""}. Try again.`;
  }

  async function saveTeams(game: Game, team1: string[], team2: string[]) {
    try {
      await updateDoc(doc(db, "clubs", clubId, "events", eventId, "games", game.id), { team1, team2 });
      setEditingTeams(null);
      setVirhe(null);
    } catch (e) {
      setVirhe(kirjoitusVirhe(e));
    }
  }
  const [saved, setSaved]         = useState<Record<string, boolean>>({});
  const [virhe, setVirhe]         = useState<string | null>(null);
  const [duprAction, setDuprAction] = useState<Record<string, "deleting"|"resubmitting"|"done"|"error">>({});

  async function saveScore(game: Game) {
    const s = scores[game.id];
    if (!s || (s.s1 === "" && s.s2 === "")) return;
    try {
      await updateDoc(doc(db, "clubs", clubId, "events", eventId, "games", game.id), {
        score1: Number(s.s1), score2: Number(s.s2), playedAt: new Date().toISOString(),
      });
      setVirhe(null);
      setSaved(prev => ({ ...prev, [game.id]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [game.id]: false })), 2000);
    } catch (e) {
      setVirhe(kirjoitusVirhe(e));
    }
  }

  async function removeFromDupr(gameId: string) {
    setDuprAction(p => ({ ...p, [gameId]: "deleting" }));
    try {
      await duprDeleteMatch({ clubId, eventId, gameId });
      setDuprAction(p => ({ ...p, [gameId]: "done" }));
      setTimeout(() => setDuprAction(p => { const n = { ...p }; delete n[gameId]; return n; }), 1500);
    } catch {
      setDuprAction(p => ({ ...p, [gameId]: "error" }));
      setTimeout(() => setDuprAction(p => { const n = { ...p }; delete n[gameId]; return n; }), 2500);
    }
  }

  async function resubmitToDupr(game: Game) {
    setDuprAction(p => ({ ...p, [game.id]: "resubmitting" }));
    try {
      // 1. Tallenna muutettu tulos ensin Firestoreen
      const s = scores[game.id];
      if (s && (s.s1 !== "" || s.s2 !== "")) {
        await updateDoc(doc(db, "clubs", clubId, "events", eventId, "games", game.id), {
          score1: Number(s.s1), score2: Number(s.s2), playedAt: new Date().toISOString(),
        });
      }
      // 2. Poista vanha DUPR-merkintä
      await duprDeleteMatch({ clubId, eventId, gameId: game.id });
      // 3. Odota 3s — DUPR tarvitsee hetken deletion prosessointiin
      await new Promise(resolve => setTimeout(resolve, 3000));
      // 4. Lähetä uudelleen uudella tuloksella
      await duprSubmitEventMatches({ clubId, eventId });
      setDuprAction(p => ({ ...p, [game.id]: "done" }));
      setTimeout(() => setDuprAction(p => { const n = { ...p }; delete n[game.id]; return n; }), 2000);
    } catch {
      setDuprAction(p => ({ ...p, [game.id]: "error" }));
      setTimeout(() => setDuprAction(p => { const n = { ...p }; delete n[game.id]; return n; }), 2500);
    }
  }

  const sorted = [...games].sort((a, b) => (a.round ?? 0) - (b.round ?? 0));
  const rounds = Array.from(new Set(sorted.map(g => g.round)));

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
        {lang === "fi" ? "Ottelut" : "Matches"} ({games.length})
      </div>

      {/* Kesken jäänyt kierto ei ole tasapuolinen: osa pareista ehtii
          pelata yhdessä kahdesti ja osa kerran. Sanotaan se illan
          aikana, kun asialle voi vielä tehdä jotain — liigaillassa
          kierron ulkopuolelle jäävät ottelut eivät kerrytä kausipisteitä
          (klubin päätös 2.11.2026, utils/league.ts). */}
      {vajaa > 0 && (
        <div style={{
          fontSize: 11, lineHeight: 1.55, color: "#b45309",
          background: "rgba(180,83,9,0.08)", border: "1px solid rgba(180,83,9,0.25)",
          borderRadius: 8, padding: "8px 10px", marginBottom: 10,
        }}>
          {lang === "fi"
            ? `⟳ Kierto on kesken — pelatkaa vielä ${vajaa} ${vajaa === 1 ? "kierros" : "kierrosta"}, niin jokainen pelaa jokaisen kanssa yhtä monta kertaa.`
              + (seasonId ? " Kesken jäävän kierron ottelut eivät kerrytä kausipisteitä." : "")
            : `⟳ The rotation is unfinished — play ${vajaa} more ${vajaa === 1 ? "round" : "rounds"} so everyone partners everyone equally often.`
              + (seasonId ? " Matches in an unfinished rotation do not count towards the season." : "")}
        </div>
      )}

      {/* Tallennus voi epäonnistua oikeuksien tai yhteyden takia, ja
          silloin se on sanottava. Hiljainen epäonnistuminen näyttää
          siltä kuin nappi ei toimisi. */}
      {virhe && (
        <div style={{
          fontSize: 12, lineHeight: 1.5, color: "var(--sl-error)",
          background: "var(--sl-soft)", border: "1px solid var(--sl-error)",
          borderRadius: 8, padding: "9px 11px", marginBottom: 10,
        }}>
          {virhe}
        </div>
      )}

      {/* Pienellä porukalla päät eivät mene tasan, eikä se ole arvonnan
          vika: neljän pelaajan kolmesta kierroksesta ei ole olemassa
          yhtäkään tasaista puolijakoa. Kentällä se korjataan vaihtamalla
          päitä kesken pelin — sama kuin virallisessa ratkaisuerässä. */}
      {sidesUneven && (
        <div style={{
          fontSize: 11, lineHeight: 1.55, color: C.text, background: C.soft,
          border: `1px solid ${C.border}`, borderRadius: 8,
          padding: "8px 10px", marginBottom: 10,
        }}>
          {lang === "fi"
            ? "⇄ Näin pienellä porukalla päät eivät mene tasan — joku pelaa kaikki ottelunsa samassa päässä. Vaihtakaa päitä kesken pelin, kun ensimmäinen pari saa 6 pistettä."
            : "⇄ With a group this small the ends cannot come out even — someone plays every match at the same end. Switch ends mid-game, when the first pair reaches 6 points."}
        </div>
      )}

      {rounds.map(round => (
        <div key={round}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: C.muted }}>{lang === "fi" ? "Kierros" : "Round"} {round}</span>
            {sorted.some(g => g.round === round && g.isExtra) && (
              <span style={{ background: "#dcfce7", color: "#16a34a", borderRadius: 6, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>EXTRA</span>
            )}
          </div>
          {sorted.filter(g => g.round === round).map(g => {
            const isSaved  = saved[g.id];
            const hasScore = g.score1 != null && g.score2 != null;
            const duprSent = !!g.duprMatchCode || !!g.duprSubmittedAt;
            const action   = duprAction[g.id];
            return (
              <div key={g.id} style={{
                background: hasScore ? "var(--sl-success-a12)" : C.soft,
                borderRadius: 10, padding: "10px 12px", marginBottom: 6,
                border: hasScore ? `1px solid ${C.border}` : "none",
              }}>
                {/* Pisteet + tallenna */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <span style={{ flex: 1, fontWeight: 600, color: C.text }}>{g.team1.join(" & ")}</span>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <input type="number" min="0" max="99"
                      value={scores[g.id]?.s1 ?? (g.score1 ?? "")}
                      onChange={e => setScores(s => ({ ...s, [g.id]: { s1: e.target.value, s2: s[g.id]?.s2 ?? String(g.score2 ?? "") } }))}
                      style={{ width: 44, padding: "4px 6px", textAlign: "center", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 700 }} />
                    <span style={{ fontWeight: 700, color: C.muted }}>—</span>
                    <input type="number" min="0" max="99"
                      value={scores[g.id]?.s2 ?? (g.score2 ?? "")}
                      onChange={e => setScores(s => ({ ...s, [g.id]: { s1: s[g.id]?.s1 ?? String(g.score1 ?? ""), s2: e.target.value } }))}
                      style={{ width: 44, padding: "4px 6px", textAlign: "center", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 700 }} />
                  </div>
                  <span style={{ flex: 1, fontWeight: 600, color: C.text, textAlign: "right" }}>{g.team2.join(" & ")}</span>
                  <button onClick={() => saveScore(g)} style={{
                    ...btn, padding: "4px 10px", fontSize: 13, marginLeft: 4,
                    background: isSaved ? "#16a34a" : C.navy, color: C.white,
                    transition: "background 0.3s",
                  }}>
                    {isSaved ? "✓" : "→"}
                  </button>
                </div>

                {/* Kumpi pari kummassa päässä. Vasen nimi on team1 eli
                    puoli A, joten rivi asettuu nimien alle sellaisenaan. */}
                {sides && (
                  <div style={{ display: "flex", fontSize: 10, color: C.muted, marginTop: 3 }}>
                    <span style={{ flex: 1 }}>⬅ {sides.a}</span>
                    <span style={{ flex: 1, textAlign: "right" }}>{sides.b} ➡</span>
                  </div>
                )}

                {/* Pelaajien korjaus. DUPR:iin lähetetyssä ottelussa ei: DUPR
                    tuntee sen vanhoilla pelaajilla, joten se poistetaan ensin. */}
                {editingTeams === g.id ? (
                  <MatchPlayersEditor
                    names={names} team1={g.team1} team2={g.team2} finished={finished} lang={lang}
                    onSave={(t1, t2) => saveTeams(g, t1, t2)} onCancel={() => setEditingTeams(null)}
                  />
                ) : !duprSent ? (
                  <button onClick={() => setEditingTeams(g.id)} style={{
                    marginTop: 6, fontSize: 10, padding: "2px 8px", borderRadius: 5, cursor: "pointer", fontWeight: 700,
                    border: `1px solid ${C.border}`, background: "transparent", color: C.muted,
                  }}>
                    ✏️ {lang === "fi" ? "Korjaa pelaajat" : "Fix players"}
                  </button>
                ) : null}

                {/* DUPR-status + toimintanapit */}
                {duprSent && (
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, color: "#2563eb", background: "#eff6ff", borderRadius: 4, padding: "2px 7px", fontWeight: 700 }}>
                      {g.duprMatchCode ? "🏆 DUPR ✓" : "📤 DUPR"}
                    </span>
                    {action === "done" && (
                      <span style={{ fontSize: 10, color: "#16a34a", fontWeight: 700 }}>✓ {lang === "fi" ? "Valmis" : "Done"}</span>
                    )}
                    {action === "error" && (
                      <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 700 }}>❌ {lang === "fi" ? "Virhe" : "Error"}</span>
                    )}
                    {!action && (
                      <>
                        <button
                          onClick={() => resubmitToDupr(g)}
                          style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontWeight: 700 }}
                        >
                          🔄 {lang === "fi" ? "Päivitä DUPR" : "Re-submit"}
                        </button>
                        <button
                          onClick={() => removeFromDupr(g.id)}
                          style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", fontWeight: 700 }}
                        >
                          🗑 {lang === "fi" ? "Poista DUPR:sta" : "Remove from DUPR"}
                        </button>
                      </>
                    )}
                    {(action === "deleting" || action === "resubmitting") && (
                      <span style={{ fontSize: 10, color: C.muted }}>
                        ⏳ {action === "deleting"
                          ? (lang === "fi" ? "Poistetaan..." : "Removing...")
                          : (lang === "fi" ? "Päivitetään..." : "Resubmitting...")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Scoreboard ────────────────────────────────────────────────
function Scoreboard({ games, participants, lang = "fi" }: { games: Game[]; participants: Participant[]; lang?: "fi"|"en" }) {
  const played = games.filter(g => gameResult(g) !== "unplayed");
  if (played.length === 0) return null;

  // Laskenta, sijoitus ja ulkoasu ovat NightStandingsissa, jotta
  // pelaaja ja ylläpitäjä näkevät saman illan samalla tavalla. Tässä oli
  // oma toteutus, ja pelaajan puolella toinen — ne ehtivät erkaantua.
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
        {lang === "fi" ? "Pistetaulukko" : "Leaderboard"}
      </div>
      <NightStandings games={played} names={participants.map(p => p.displayName)} lang={lang} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PLAYER EDIT FORM  (top-level — ei sisäkkäinen, jotta fokus säilyy)
// ══════════════════════════════════════════════════════════════
interface PlayerFormProps {
  name: string; setName: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  duprId: string; setDuprId: (v: string) => void;
  ppr: string; setPpr: (v: string) => void;
  utr: string; setUtr: (v: string) => void;
  pprId: string; setPprId: (v: string) => void;
  utrId: string; setUtrId: (v: string) => void;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  onSendInvite?: () => void;
  inviteSending?: boolean;
  inviteSent?: boolean;
  lang?: "fi"|"en";
}
function PlayerEditForm({ name, setName, email, setEmail, phone, setPhone, duprId, setDuprId, ppr, setPpr, utr, setUtr, pprId, setPprId, utrId, setUtrId, saving, onSave, onCancel, onSendInvite, inviteSending, inviteSent, lang = "fi" }: PlayerFormProps) {
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
        {lang === "en" ? "Basic info" : "Perustiedot"}
      </div>
      <input value={name}  onChange={e => setName(e.target.value)}  placeholder={lang === "en" ? "Name *" : "Nimi *"}  style={input} />
      <input value={email} onChange={e => setEmail(e.target.value)} placeholder={lang === "en" ? "Email" : "Sähköposti"} type="email" style={input} />
      <input value={phone} onChange={e => setPhone(e.target.value)} placeholder={lang === "en" ? "Phone (e.g. +358401234567)" : "Puhelin (esim. +358401234567)"} type="tel" style={input} />

      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", margin: "10px 0 8px" }}>
        {lang === "en" ? "Ratings & IDs" : "Ratingit & tunnukset"}
      </div>

      {/* DUPR */}
      <div style={{ background: "#eff6ff", borderRadius: 10, padding: "10px 12px", marginBottom: 8, border: "1px solid #bfdbfe" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", marginBottom: 6 }}>🏆 DUPR — Pickleball</div>
        {/* Tunnusta ei voi enaa kirjoittaa kasin.
            
            Vaarin kirjoitettu tunnus lahetti otteluita tuntemattoman pelaajan
            tilille Kaliforniaan; tilinhaltija ei ollut koskaan kuullut
            Spinloopista. DUPR vahvisti 29.9.2026 ettei manuaalinen syotto ole
            tuettu tapa lainkaan — pelaajan on yhdistettava tilinsa itse
            SSO:n kautta, ja vain silloin rajapinta antaa lukea ratingin.
            
            Poistonappi jaa: vaara tunnus on voitava siivota. Se on turvallinen
            suunta, koska sita ei voi kayttaa tunnuksen sijoittamiseen. */}
        {duprId ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{
              flex: 1, fontFamily: "monospace", letterSpacing: 1,
              fontSize: 14, fontWeight: 700, color: "#1e3a8a",
            }}>
              {duprId}
            </span>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(lang === "en"
                  ? "Remove this DUPR ID? The player can reconnect from their own profile."
                  : "Poistetaanko DUPR-tunnus? Pelaaja voi yhdistää tilinsä uudelleen omasta profiilistaan.")) {
                  setDuprId("");
                }
              }}
              style={{
                background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
                borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 700,
                cursor: "pointer", flexShrink: 0,
              }}>
              {lang === "en" ? "Remove" : "Poista"}
            </button>
          </div>
        ) : (
          <div style={{
            fontSize: 12, color: "#64748b", fontStyle: "italic", marginBottom: 6,
          }}>
            {lang === "en" ? "Not connected" : "Ei yhdistetty"}
          </div>
        )}
        <div style={{ fontSize: 10, color: "#3b82f6", lineHeight: 1.5 }}>
          {lang === "en"
            ? "A DUPR ID cannot be entered here. The player connects their own account from their profile — that is the only way DUPR lets us read a rating or submit their matches. Matches are not sent for players who have not connected."
            : "DUPR-tunnusta ei voi syöttää tästä. Pelaaja yhdistää tilinsä itse omasta profiilistaan — vain silloin DUPR antaa lukea ratingin tai lähettää hänen ottelunsa. Yhdistämättömien pelaajien otteluita ei lähetetä."}
        </div>
      </div>

      {/* PPR */}
      <div style={{ background: C.soft, borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", marginBottom: 6 }}>🏓 PPR — Padel</div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Rating (1.0–10.0)</div>
            <input value={ppr} onChange={e => setPpr(e.target.value)}
              placeholder={lang === "en" ? "e.g. 5.0" : "esim. 5.0"} type="number" step="0.1" min="1" max="10"
              style={{ ...input, marginBottom: 0 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{lang === "en" ? "PPR ID" : "PPR-tunnus"}</div>
            <input value={pprId} onChange={e => setPprId(e.target.value)}
              placeholder={lang === "en" ? "e.g. jpartinen" : "esim. jpartinen"}
              style={{ ...input, marginBottom: 0 }} />
          </div>
        </div>
      </div>

      {/* UTR */}
      <div style={{ background: C.soft, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", marginBottom: 6 }}>🎾 UTR — Tennis</div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Rating (1.0–16.0)</div>
            <input value={utr} onChange={e => setUtr(e.target.value)}
              placeholder={lang === "en" ? "e.g. 4.0" : "esim. 4.0"} type="number" step="0.1" min="1" max="16"
              style={{ ...input, marginBottom: 0 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{lang === "en" ? "UTR ID" : "UTR-tunnus"}</div>
            <input value={utrId} onChange={e => setUtrId(e.target.value)}
              placeholder={lang === "en" ? "e.g. jukka-partinen" : "esim. jukka-partinen"}
              style={{ ...input, marginBottom: 0 }} />
          </div>
        </div>
      </div>

      {/* Kutsu sähköpostilla */}
      {onSendInvite && (
        <button
          onClick={onSendInvite}
          disabled={inviteSending || !email.trim()}
          style={{
            ...btn, width: "100%", marginBottom: 8,
            background: inviteSent ? "#f0fdf4" : "#eff6ff",
            color: inviteSent ? "#16a34a" : "#1d4ed8",
            border: `1px solid ${inviteSent ? "#bbf7d0" : "#bfdbfe"}`,
            opacity: (!email.trim() || inviteSending) ? 0.5 : 1,
            fontSize: 13,
          }}
        >
          {inviteSending
            ? (lang === "en" ? "⏳ Sending..." : "⏳ Lähetetään...")
            : inviteSent
              ? (lang === "en" ? "✅ Invite sent!" : "✅ Kutsu lähetetty!")
              : (lang === "en" ? "✉️ Send invite link" : "✉️ Lähetä kutsu sähköpostilla")}
        </button>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onSave} disabled={saving || !name.trim()} style={{
          ...btn, flex: 1, background: C.navy, color: C.white,
          opacity: (!name.trim() || saving) ? 0.5 : 1,
        }}>
          {saving ? (lang === "en" ? "Saving..." : "Tallennetaan...") : (lang === "en" ? "💾 Save" : "💾 Tallenna")}
        </button>
        <button onClick={onCancel} style={{ ...btn, flex: 1, background: C.soft, color: C.muted }}>
          {lang === "en" ? "Cancel" : "Peruuta"}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PLAYERS TAB
// ══════════════════════════════════════════════════════════════
function PlayersTab({ clubId, clubName, players, onRefresh, lang = "fi" }: { clubId: string; clubName: string; players: Player[]; onRefresh: () => void; lang?: "fi"|"en" }) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding]       = useState(false);

  const [draftName,   setDraftName]   = useState("");
  const [draftEmail,  setDraftEmail]  = useState("");
  const [draftPhone,  setDraftPhone]  = useState("");
  const [draftDuprId, setDraftDuprId] = useState("");
  const [draftPpr,    setDraftPpr]    = useState("");
  const [draftUtr,    setDraftUtr]    = useState("");
  const [draftPprId,  setDraftPprId]  = useState("");
  const [draftUtrId,  setDraftUtrId]  = useState("");
  const [saving,        setSaving]        = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSentId,  setInviteSentId]  = useState<string | null>(null);
  // Järjestäjät (docs/roolit.md). Lista asuu private-alikokoelmassa, jota
  // asiakas ei voi lukea, joten se haetaan funktion kautta.
  const [organizers, setOrganizers] = useState<Set<string>>(new Set());
  useEffect(() => {
    listClubRolesFn({ clubId })
      .then(res => setOrganizers(new Set(res.data.organizers)))
      .catch(err => console.error("listClubRoles", err));
  }, [clubId]);
  async function setOrganizer(playerId: string, organizer: boolean) {
    const res = await setClubRoleFn({ clubId, playerId, organizer });
    setOrganizers(new Set(res.data.organizers));
  }

  function startEdit(p: Player) {
    setEditingId(p.id);
    setAdding(false);
    setDraftName(p.name);
    setDraftEmail(p.email || "");
    setDraftPhone(p.phone || "");
    setDraftDuprId(p.duprId || "");
    setDraftPpr(p.ratings?.padel?.toString() || "");
    setDraftUtr(p.ratings?.tennis?.toString() || "");
    setDraftPprId(p.pprId || "");
    setDraftUtrId(p.utrId || "");
  }

  function startAdd() {
    setAdding(true);
    setEditingId(null);
    setDraftName(""); setDraftEmail(""); setDraftPhone("");
    setDraftDuprId("");
    setDraftPpr(""); setDraftUtr("");
    setDraftPprId(""); setDraftUtrId("");
  }

  async function saveEdit(playerId: string) {
    if (!draftName.trim()) return;
    setSaving(true);
    const ratings: Record<string, number> = {};
    if (draftPpr) ratings.padel  = parseFloat(draftPpr);
    if (draftUtr) ratings.tennis = parseFloat(draftUtr);
    await updateDoc(doc(db, "clubs", clubId, "players", playerId), {
      name: draftName.trim(),
      email: draftEmail.trim(),
      phone: draftPhone.trim(),
      ...(draftDuprId.trim() ? { duprId: draftDuprId.trim() } : {}),
      pprId: draftPprId.trim(),
      utrId: draftUtrId.trim(),
      ...(Object.keys(ratings).length > 0 ? { ratings } : {}),
    });
    setSaving(false);
    setEditingId(null);
    onRefresh();
  }

  async function saveNew() {
    if (!draftName.trim()) return;
    setSaving(true);
    const ratings: Record<string, number> = {};
    if (draftPpr) ratings.padel  = parseFloat(draftPpr);
    if (draftUtr) ratings.tennis = parseFloat(draftUtr);
    const ref = doc(collection(db, "clubs", clubId, "players"));
    await setDoc(ref, {
      name: draftName.trim(),
      email: draftEmail.trim(),
      phone: draftPhone.trim(),
      ...(draftDuprId.trim() ? { duprId: draftDuprId.trim() } : {}),
      pprId: draftPprId.trim(),
      utrId: draftUtrId.trim(),
      ratings,
      createdAt: new Date().toISOString(),
    });
    setSaving(false);
    setAdding(false);
    onRefresh();
  }

  async function deletePlayer(p: Player) {
    if (!window.confirm(lang === "en" ? `Delete ${p.name}?` : `Poistetaanko ${p.name}?`)) return;
    await deleteDoc(doc(db, "clubs", clubId, "players", p.id));
    onRefresh();
  }

  async function sendInvite(p: Player) {
    const email = editingId === p.id ? draftEmail.trim() : p.email?.trim();
    if (!email) return;
    setInviteSending(true);
    try {
      await sendPlayerInviteLink({
        clubId,
        playerId: p.id,
        playerName: draftName.trim() || p.name,
        email,
        clubName,
        // Kutsuviesti on aina suomeksi. Aiemmin tähän meni adminin oma
        // käyttöliittymäkieli, jolloin EN-tilassa pelaaja sai englannin-
        // kielisen viestin mutta kutsun hyväksymissivu oli suomeksi.
        // Vastaanottajan kieltä ei tiedetä, ja yleiskieli on suomi.
        lang: "fi",
      });
      setInviteSentId(p.id);
      // Reset after 4 s
      setTimeout(() => setInviteSentId(prev => prev === p.id ? null : prev), 4000);
    } catch (err) {
      alert(lang === "en"
        ? `Failed to send invite: ${err instanceof Error ? err.message : String(err)}`
        : `Kutsun lähetys epäonnistui: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInviteSending(false);
    }
  }

  const formProps: PlayerFormProps = {
    name: draftName, setName: setDraftName,
    email: draftEmail, setEmail: setDraftEmail,
    phone: draftPhone, setPhone: setDraftPhone,
    duprId: draftDuprId, setDuprId: setDraftDuprId,
    ppr: draftPpr, setPpr: setDraftPpr,
    utr: draftUtr, setUtr: setDraftUtr,
    pprId: draftPprId, setPprId: setDraftPprId,
    utrId: draftUtrId, setUtrId: setDraftUtrId,
    saving,
    onSave: () => {},
    onCancel: () => {},
    lang,
  };

  return (
    <div>
      {/* Otsikko + lisää */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {players.length} {lang === "fi" ? "pelaajaa" : "players"}
        </div>
        <button onClick={startAdd} style={{ ...btn, background: C.navy, color: C.white, fontSize: 12, padding: "8px 16px" }}>
          {lang === "fi" ? "+ Lisää pelaaja" : "+ Add player"}
        </button>
      </div>

      {/* Lisää uusi pelaaja */}
      {adding && (
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, fontFamily: F.heading }}>{lang === "fi" ? "Uusi pelaaja" : "New player"}</div>
          <PlayerEditForm {...formProps} onSave={saveNew} onCancel={() => setAdding(false)} />
        </div>
      )}

      {/* Pelaajalistat */}
      {players.map(p => (
        <div key={p.id} style={card}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar
              name={p.name} photoUrl={p.photoUrl} size={44}
              background={`linear-gradient(135deg, ${C.navy}, #2a5c8f)`}
              color={C.white} style={{ fontWeight: 900 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                {p.name}
                {organizers.has(p.id) && <span style={{ fontSize: 10, fontWeight: 700, color: C.navy, marginLeft: 6 }}>📋 {lang === "en" ? "Organizer" : "Järjestäjä"}</span>}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                {p.email || <span style={{ color: "#fbbf24" }}>Ei sähköpostia</span>}
              </div>
              {p.phone && <div style={{ fontSize: 11, color: C.muted }}>{p.phone}</div>}
              <div style={{ fontSize: 10, color: C.border, marginTop: 2 }}>
                {p.passkeyCredId ? "🔐 Biometria · " : ""}
                {p.lastSeen ? `Viimeksi ${new Date(p.lastSeen).toLocaleDateString("fi-FI")}` : "Ei kirjauksia"}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              {/* DUPR API -luku (prioriteetti) tai manuaalisesti syötetty */}
              {(p.duprDoublesRating || p.ratings?.pickleball) && (
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.gold }}>
                    DUPR {(p.duprDoublesRating ?? p.ratings?.pickleball)?.toFixed(2)}
                  </span>
                  {p.duprDoublesRating && (
                    <span style={{ fontSize: 9, color: "#2563eb", marginLeft: 4 }}>API</span>
                  )}
                </div>
              )}
              {/* Voitot / häviöt */}
              {((p.wins ?? 0) + (p.losses ?? 0)) > 0 && (
                <span style={{ fontSize: 10, color: C.muted }}>
                  {p.wins ?? 0}V / {p.losses ?? 0}H
                </span>
              )}
              <button onClick={() => editingId === p.id ? setEditingId(null) : startEdit(p)}
                style={{ ...btn, padding: "5px 12px", fontSize: 11, background: C.soft, color: C.navy }}>
                {editingId === p.id ? (lang === "en" ? "✕ Close" : "✕ Sulje") : (lang === "en" ? "✏️ Edit" : "✏️ Muokkaa")}
              </button>
            </div>
          </div>

          {editingId === p.id && (
            <PlayerEditForm
              {...formProps}
              onSave={() => saveEdit(p.id)}
              onCancel={() => setEditingId(null)}
              onSendInvite={() => sendInvite(p)}
              inviteSending={inviteSending}
              inviteSent={inviteSentId === p.id}
            />
          )}

          {editingId === p.id && (
            <OrganizerRoleToggle
              isOrganizer={organizers.has(p.id)}
              hasEmail={!!(draftEmail.trim() || p.email?.trim())}
              verifiedEver={!!p.passkeyCredId}
              onToggle={next => setOrganizer(p.id, next)}
              onSendInvite={() => sendInvite(p)}
              lang={lang}
            />
          )}

          {editingId === p.id && (
            <button onClick={() => deletePlayer(p)}
              style={{ ...btn, width: "100%", marginTop: 8, background: "#fef2f2", color: C.error, border: `1px solid #fecaca`, fontSize: 12 }}>
              🗑 {lang === "en" ? "Delete player" : "Poista pelaaja"}
            </button>
          )}
        </div>
      ))}

      {players.length === 0 && !adding && (
        <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
          {lang === "en" ? "No players yet." : "Ei pelaajia vielä."}<br />
          <span style={{ fontSize: 12 }}>
            {lang === "en"
              ? "Add manually or players register automatically when they log in."
              : "Lisää manuaalisesti tai pelaajat rekisteröityvät automaattisesti kirjautuessaan."}
          </span>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// HISTORY TAB
// ══════════════════════════════════════════════════════════════
function HistoryTab({ clubId, events, lang = "fi" }: { clubId: string; events: ClubEvent[]; lang?: "fi"|"en" }) {
  const finished = events.filter(e => e.status === "finished");
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
        {finished.length} {lang === "fi" ? "päättynyttä tapahtumaa" : "ended events"}
      </div>
      {finished.map(ev => (
        <HistoryEventCard key={ev.id} event={ev} clubId={clubId} lang={lang} />
      ))}
      {finished.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
          {lang === "fi" ? "Ei päättyneitä tapahtumia vielä." : "No ended events yet."}
        </div>
      )}
    </div>
  );
}

function HistoryEventCard({ event: ev, clubId, lang = "fi" }: { event: ClubEvent; clubId: string; lang?: "fi"|"en" }) {
  const [expanded, setExpanded]         = useState(false);
  const [games, setGames]               = useState<Game[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading]           = useState(false);

  // Kuunnellaan avattuna, kuten FinishedEventCard. Aiemmin ottelut
  // haettiin kerran (getDocs), joten tuloksen tai pelaajien korjaus ei
  // näkynyt kortissa: se näytti siltä, ettei tallennus onnistunut, ja
  // editori aukesi uudelleen vanhoilla tiedoilla.
  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    const unsub1 = onSnapshot(
      collection(db, "clubs", clubId, "events", ev.id, "games"),
      snap => { setGames(snap.docs.map(d => ({ id: d.id, ...d.data() } as Game))); setLoading(false); },
      () => setLoading(false),
    );
    const unsub2 = onSnapshot(
      collection(db, "clubs", clubId, "events", ev.id, "participants"),
      snap => setParticipants(snap.docs.map(d => ({ id: d.id, ...d.data() } as Participant))),
    );
    return () => { unsub1(); unsub2(); };
  }, [expanded, clubId, ev.id]);

  function toggle() {
    setExpanded(e => !e);
  }

  const playedCount = games.filter(g => g.score1 != null && g.score2 != null).length;

  return (
    <div style={card}>
      <div onClick={toggle} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            <PickleballMark /> {ev.venue || (lang === "fi" ? "Tapahtuma" : "Event")}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
            {new Date(ev.datetime).toLocaleDateString(lang === "en" ? "en-GB" : "fi-FI", { weekday:"long", day:"numeric", month:"long" })}
          </div>
          {ev.panos ? <div style={{ fontSize: 11, color: C.gold, marginTop: 2 }}>💰 {ev.panos} € {lang === "fi" ? "panos" : "entry fee"}</div> : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {ev.duprSubmitted && (
            <span style={{ fontSize: 10, color: "#16a34a", background: "#f0fdf4", borderRadius: 6, padding: "2px 7px", fontWeight: 700, border: "1px solid #bbf7d0" }}>✅ DUPR</span>
          )}
          {!expanded && playedCount === 0 && games.length === 0 && (
            <span style={{ fontSize: 10, color: C.muted }}>{lang === "fi" ? "Näytä pelit" : "Show matches"}</span>
          )}
          {expanded && playedCount > 0 && (
            <span style={{ fontSize: 10, color: C.muted }}>{playedCount} {lang === "fi" ? "ottelua" : "matches"}</span>
          )}
          <span style={{ fontSize: 14, color: C.muted }}>{loading ? "⏳" : expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && !loading && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          {games.length === 0
            ? <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: 12 }}>
                {lang === "fi" ? "Ei otteluja tallennettu" : "No matches recorded"}
              </div>
            : <>
                <GamesSection clubId={clubId} eventId={ev.id} games={games} lang={lang} participants={participants} finished venue={ev.venue} />
                <Scoreboard games={games} participants={participants} lang={lang} />
              </>
          }
          <RecalcPlacingsBox clubId={clubId} event={ev} games={games} participants={participants} lang={lang} />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SETTINGS TAB — Lipas & muut asetukset
// ══════════════════════════════════════════════════════════════
function SettingsTab({ clubId, lang, onChangePinClick, onRefresh, eventLimit = 3, onEventLimit }: {
  clubId: string; lang: "fi"|"en"; onChangePinClick: () => void; onRefresh?: () => void;
  eventLimit?: number; onEventLimit?: (n: number) => void;
}) {
  const [skin, setSkin] = useState<"kentta" | "classic">(() => {
    try { return localStorage.getItem("spinloop_skin") === "classic" ? "classic" : "kentta"; } catch { return "kentta"; }
  });
  // Lipas-koodit asuvat klubidokumentissa. Aiemmin ne olivat vain tässä
  // selaimessa, jolloin ne eivät näkyneet toisella koneella eivätkä
  // pelaajille lainkaan — ja koko MobilePay-nappi jäi näyttämättä.
  // localStorage on yhä mukana kertaluontoisena siirtymänä vanhoille
  // koodeille; sitä luetaan mutta ei enää kirjoiteta.
  const LS_SALI  = `spinloop_lipas_sali_${clubId}`;
  const LS_PANOS = `spinloop_lipas_panos_${clubId}`;
  const [sali,  setSali]  = useState("");
  const [panos, setPanos] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [editSali,  setEditSali]  = useState(false);
  const [editPanos, setEditPanos] = useState(false);
  const [draftSali,  setDraftSali]  = useState("");
  const [draftPanos, setDraftPanos] = useState("");

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "clubs", clubId)).then(async snap => {
      if (!alive) return;
      const d = snap.data() || {};
      const oldSali  = localStorage.getItem(LS_SALI)  || "";
      const oldPanos = localStorage.getItem(LS_PANOS) || "";
      const s = d.lipasSali  ?? oldSali;
      const p = d.lipasPanos ?? oldPanos;
      setSali(s); setPanos(p);
      setDraftSali(s); setDraftPanos(p);
      setLoaded(true);
      // Siirrä vanhat selainkohtaiset koodit klubille, jotta niitä ei
      // tarvitse syöttää uudelleen. Tehdään vain jos klubilla ei vielä ole.
      const migrate: Record<string, string> = {};
      if (d.lipasSali  === undefined && oldSali)  migrate.lipasSali  = oldSali;
      if (d.lipasPanos === undefined && oldPanos) migrate.lipasPanos = oldPanos;
      if (Object.keys(migrate).length > 0) {
        try { await updateDoc(doc(db, "clubs", clubId), migrate); } catch { /* ei kriittinen */ }
      }
    }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [clubId, LS_SALI, LS_PANOS]);
  // Natiivikuoressa selaimen Notification-rajapintaa ei ole, ja lupatila
  // kysytaan FirebaseMessagingilta asynkronisesti.
  const [notifGranted, setNotifGranted] = useState(() => webNotificationPermission() === "granted");
  useEffect(() => { notificationsGranted().then(setNotifGranted); }, []);

  // Osoitetta ei saa isontaa: polun kirjainkoko voi olla merkitsevä.
  // Vain vanha lyhytkoodi isonnettiin, ja se tunnistus oli kirjainkoolle
  // herkkä — liitetty "HTTPS://..." meni väärään haaraan.
  // MobilePay-linkki tallennetaan aina toimivassa muodossa (pienet kirjaimet,
  // .../box/<uuid>/pay-in), ks. src/utils/mobilepay.ts.
  const normalize = normalizeLipas;

  async function saveLipas(field: "lipasSali" | "lipasPanos", value: string) {
    try {
      await updateDoc(doc(db, "clubs", clubId), { [field]: value });
      return true;
    } catch (err) {
      console.error("Lipas-koodin tallennus epäonnistui", err);
      window.alert(lang === "fi"
        ? "Koodin tallennus epäonnistui. Tarkista yhteys ja yritä uudelleen."
        : "Could not save the code. Check your connection and try again.");
      return false;
    }
  }

  async function saveSali() {
    const v = normalize(draftSali);
    if (!await saveLipas("lipasSali", v)) return;
    setSali(v); setEditSali(false);
  }
  async function savePanos() {
    const v = normalize(draftPanos);
    if (!await saveLipas("lipasPanos", v)) return;
    setPanos(v); setEditPanos(false);
  }


  // Palauttaa null jos arvosta ei saa toimivaa linkkiä. Aiemmin tässä oli
  // kolmas haara `mobilepay://lipas/<koodi>`, joka rakennettiin lyhyestä
  // Lipas-koodista. Se ei toimi: MobilePay vastaa "Tuntematon QR-koodi,
  // QR-koodia ei ole aktivoitu". Kenttä myös pyysi nimenomaan sitä koodia,
  // eli käyttöliittymä ohjasi syöttämään arvon joka ei voinut toimia.
  const makeLink = mobilePayLink;

  function shortDisplay(code: string) {
    if (!code) return "—";
    if (code.toLowerCase().startsWith("http")) {
      // Näytä vain UUID-osa URL:sta: .../box/UUID/pay-in → UUID
      const m = code.match(/box\/([^/]+)/i);
      return m ? m[1].toUpperCase() : "✓ URL tallennettu";
    }
    return code;
  }

  const lipasCards = [
    { title: lang === "fi" ? "Salivuoro" : "Court Booking", icon: "🏟️", color: "#38bdf8", code: sali, editing: editSali, draft: draftSali, setDraft: setDraftSali, setEditing: setEditSali, save: saveSali },
    { title: lang === "fi" ? "Pelipanos" : "Entry Fee",     icon: "🎯", color: "#f59e0b", code: panos, editing: editPanos, draft: draftPanos, setDraft: setDraftPanos, setEditing: setEditPanos, save: savePanos },
  ];

  return (
    <div>
      {/* PIN-vaihto */}
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
        🔑 {lang === "fi" ? "Turvallisuus" : "Security"}
      </div>
      <div style={{ ...card, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F.heading }}>
            {lang === "fi" ? "Admin PIN-koodi" : "Admin PIN code"}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {lang === "fi" ? "Vaihda PIN omaksi turvallisemmaksi koodiksi" : "Change PIN to your own secure code"}
          </div>
        </div>
        <button onClick={onChangePinClick} style={{ ...btn, background: C.navy, color: C.white, fontSize: 12, whiteSpace: "nowrap" }}>
          {lang === "fi" ? "Vaihda PIN" : "Change PIN"}
        </button>
      </div>

      {/* Ulkoasu */}
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
        🎨 {lang === "fi" ? "Ulkoasu" : "Appearance"}
      </div>
      <div style={{ ...card, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F.heading }}>{lang === "fi" ? "Väriteema" : "Color theme"}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{lang === "fi" ? "Kenttä: kentän sininen, keittiön vihreä ja pallon keltainen" : "Court: court blue, kitchen green and ball yellow"}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {(["kentta", "classic"] as const).map(k => (
            <button key={k} onClick={() => {
              setSkin(k);
              try { localStorage.setItem("spinloop_skin", k); } catch { /* ei kriittinen */ }
              if (k === "classic") document.documentElement.dataset.skin = "classic";
              else delete document.documentElement.dataset.skin;
            }} style={{ ...btn, padding: "8px 12px", fontSize: 12, background: skin === k ? C.navy : C.soft, color: skin === k ? C.white : C.text }}>
              {k === "kentta" ? (lang === "fi" ? "Kenttä" : "Court") : (lang === "fi" ? "Klassinen" : "Classic")}
            </button>
          ))}
        </div>
      </div>

      {/* Tapahtumalistan pituus */}
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
        📅 {lang === "fi" ? "Tapahtumalista" : "Event list"}
      </div>
      <div style={{ ...card, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F.heading }}>
            {lang === "fi" ? "Näytä seuraavat" : "Show the next"}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {lang === "fi" ? "Tulevat tapahtumat aikajärjestyksessä, loput Näytä kaikki -napin takana" : "Upcoming events in date order, the rest behind Show all"}
          </div>
        </div>
        <select value={eventLimit} onChange={e => onEventLimit?.(Number(e.target.value))}
          style={{ ...input, width: 90, marginBottom: 0, appearance: "auto" as React.CSSProperties["appearance"] }}>
          {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      <AnnouncementsAdmin clubId={clubId} lang={lang} />

      <SeasonSettings clubId={clubId} lang={lang} onChanged={onRefresh} />

      <VenueSettings clubId={clubId} lang={lang} />

      <ClubRulesAdmin clubId={clubId} lang={lang} />

      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16 }}>
        💳 {lang === "fi" ? "MobilePay Lipas-tilit" : "MobilePay Accounts"}
      </div>

      {lipasCards.map(lc => (
        <div key={lc.title} style={{ ...card, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 24 }}>{lc.icon}</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F.heading }}>{lc.title}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{lang === "fi" ? "MobilePay Lipas -linkki" : "MobilePay Box link"}</div>
            </div>
          </div>

          {lc.editing ? (
            <div>
              <input value={lc.draft} onChange={e => lc.setDraft(e.target.value)}
                placeholder="https://qr.mobilepay.fi/box/…"
                style={{ ...input, marginBottom: 6, fontFamily: "monospace", fontSize: 13 }} autoFocus />
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
                {lang === "fi"
                  ? "Liitä lippaan koko jakolinkki MobilePay-sovelluksesta (Lipas → Jaa). Pelkkä lyhyt koodi ei toimi."
                  : "Paste the full share link from the MobilePay app (Box → Share). A short code alone does not work."}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={lc.save} style={{ ...btn, flex: 1, background: C.navy, color: C.white }}>✔ {lang === "fi" ? "Tallenna" : "Save"}</button>
                <button onClick={() => lc.setEditing(false)} style={{ ...btn, flex: 1, background: C.soft, color: C.text }}>{lang === "fi" ? "Peruuta" : "Cancel"}</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: C.soft, borderRadius: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace", letterSpacing: 2, color: lc.code ? lc.color : C.muted }}>
                  {shortDisplay(lc.code || "")}
                </div>
                <button onClick={() => { lc.setDraft(lc.code); lc.setEditing(true); }}
                  disabled={!loaded}
                  style={{ ...btn, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 12, padding: "6px 14px", opacity: loaded ? 1 : 0.5 }}>
                  {!loaded
                    ? (lang === "fi" ? "Ladataan…" : "Loading…")
                    : lc.code ? (lang === "fi" ? "Muuta" : "Edit") : (lang === "fi" ? "+ Lisää" : "+ Add")}
                </button>
              </div>
              {lc.code && !makeLink(lc.code) && (
                <div style={{ padding: "10px 12px", borderRadius: 10, background: "#fffbeb", border: "1px solid #fcd34d", fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
                  {lang === "fi"
                    ? <>⚠️ Tämä on vanha lyhytkoodi, eikä se toimi. MobilePay vastaa siihen <em>"Tuntematon QR-koodi"</em>. Paina <strong>Muuta</strong> ja liitä tilalle lippaan koko jakolinkki.</>
                    : <>⚠️ This is an old short code and it does not work. MobilePay answers <em>"Unknown QR code"</em>. Press <strong>Edit</strong> and paste the box's full share link instead.</>}
                </div>
              )}
              {lc.code && makeLink(lc.code) && (
                <a href={makeLink(lc.code) as string} target="_blank" rel="noopener noreferrer" style={{
                  display: "block", textAlign: "center", padding: "12px 0",
                  background: `linear-gradient(135deg, #5a3fd8, #3b82f6)`,
                  color: "#fff", borderRadius: 12, fontWeight: 700, fontSize: 13,
                  textDecoration: "none", fontFamily: F.heading, letterSpacing: 1,
                }}>
                  📱 {lang === "fi" ? "Testaa MobilePay-linkki" : "Test MobilePay link"}
                </a>
              )}
            </div>
          )}
        </div>
      ))}

      <div style={{ ...card, background: "rgba(244,164,38,0.06)", border: "1px solid rgba(244,164,38,0.2)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.gold, marginBottom: 6 }}>ℹ️ {lang === "fi" ? "Miten toimii?" : "How does it work?"}</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
          {lang === "fi"
            ? "Liitä tähän lippaan koko jakolinkki MobilePay-sovelluksesta — pelkkä lyhyt koodi ei toimi. Pelaajat näkevät tapahtumassa MobilePay-napin, joka avaa lippaan. Pelaaja syöttää summan ja maksaa. Merkitse maksu vahvistetuksi ✓ osallistujalistalta."
            : "Paste the box's full share link from the MobilePay app — a short code alone does not work. Players see a MobilePay button in the event that opens the box. The player enters the amount and pays. Mark payment as confirmed ✓ in the participant list."}
        </div>
      </div>

      {/* Push-ilmoitusstatus adminille */}
      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
          🔔 {lang === "fi" ? "Push-ilmoitukset" : "PUSH NOTIFICATIONS"}
        </div>
        {notifGranted ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f0fdf4", borderRadius: 10, border: "1px solid #bbf7d0" }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>{lang === "fi" ? "Ilmoitukset päällä" : "Notifications on"}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{lang === "fi" ? "Saat ilmoituksen kun pelaaja vahvistaa osallistumisen" : "Get notified when a player confirms participation"}</div>
            </div>
          </div>
        ) : (
          <div style={{ padding: "12px 14px", background: "#fef2f2", borderRadius: 10, border: "1px solid #fecaca" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>⚠️ {lang === "fi" ? "Ilmoitukset ei käytössä" : "Notifications disabled"}</div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
              {lang === "fi"
                ? <>{`Et saa ilmoitusta kun pelaaja vahvistaa. Salli ilmoitukset selaimen asetuksista:`}<br /><strong>spinloop.app → Ilmoitukset → Salli</strong></>
                : <>{`You won't be notified when a player confirms. Allow notifications in browser settings:`}<br /><strong>spinloop.app → Notifications → Allow</strong></>}
            </div>
          </div>
        )}
      </div>

      {/* Versio & info */}
      <div style={{ ...card, textAlign: "center", padding: "18px 16px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Spinloop <span style={{ color: C.gold }}>v{APP_VERSION}</span>
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
          DeepEnd Solutions Oy
        </div>
        <a href="/privacy" style={{
          fontSize: 11, color: C.navy, textDecoration: "none",
          background: C.soft, borderRadius: 8, padding: "5px 14px",
          display: "inline-block", fontWeight: 600,
        }}>
          🔒 {lang === "fi" ? "Tietosuojakäytäntö" : "Privacy Policy"}
        </a>
      </div>
    </div>
  );
}

// ── Rating Badge ──────────────────────────────────────────────
function RatingBadge({ name, value, max }: { name: string; value: number; max: number }) {
  return (
    <div style={{
      background: C.soft, border: `1px solid ${C.border}`,
      borderRadius: 8, padding: "6px 12px", textAlign: "center",
    }}>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>{name}</div>
      <div style={{ fontSize: 16, fontWeight: 900, color: C.navy }}>{value.toFixed(2)}</div>
      <div style={{ fontSize: 9, color: C.muted }}>/ {max}</div>
    </div>
  );
}

// ── ChangePinModal ────────────────────────────────────────────
function ChangePinModal({ clubId, lang, isFirstLogin, onDone, onSkip }: {
  clubId: string;
  lang: "fi"|"en";
  isFirstLogin: boolean;
  onDone: () => void;
  onSkip: () => void;
}) {
  const [newPin,     setNewPin]     = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");
  const [done,       setDone]       = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (newPin.length < 4) {
      setError(lang === "fi" ? "PIN-koodin pitää olla vähintään 4 merkkiä." : "PIN must be at least 4 characters.");
      return;
    }
    if (newPin !== confirmPin) {
      setError(lang === "fi" ? "PIN-koodit eivät täsmää." : "PIN codes do not match.");
      return;
    }
    setSaving(true);
    try {
      // PIN asuu salaisessa alikokoelmassa. Ylläpitäjä saa kirjoittaa
      // sinne mutta ei lukea — uuden asettaminen ei vaadi vanhan
      // näkemistä, ja lukuoikeus tekisi PIN:stä taas vuodettavan.
      await setDoc(doc(db, "clubs", clubId, "private", "auth"), { adminPin: newPin }, { merge: true });

      // Merkintä siitä *että* PIN on vaihdettu kuuluu klubidokumenttiin:
      // ylläpitäjä ei saa lukea private/auth:ia, joten sieltä tätä ei
      // voisi kysyä. Mukana kulkee vain aikaleima, ei PIN. Epäonnistuminen
      // ei saa näyttää siltä että vaihto epäonnistui — PIN on jo vaihtunut,
      // ja pahin seuraus on että muistutus tulee vielä kerran.
      try {
        await updateDoc(doc(db, "clubs", clubId), { pinChangedAt: Date.now() });
      } catch { /* muistutus tulee uudestaan, PIN on silti vaihdettu */ }

      setDone(true);
      setTimeout(onDone, 1500);
    } catch {
      setError(lang === "fi" ? "Tallennus epäonnistui. Yritä uudelleen." : "Save failed. Please try again.");
    }
    setSaving(false);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      background: "rgba(22,59,104,0.88)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{ background: C.card, borderRadius: 24, padding: 28, maxWidth: 360, width: "100%" }}>
        {done ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, fontFamily: F.heading }}>
              {lang === "fi" ? "PIN vaihdettu!" : "PIN changed!"}
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 24, marginBottom: 6, textAlign: "center" }}>🔑</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, fontFamily: F.heading, textAlign: "center", marginBottom: 4 }}>
              {isFirstLogin
                ? (lang === "fi" ? "Vaihda PIN omaksesi" : "Set your own PIN")
                : (lang === "fi" ? "Vaihda PIN-koodi" : "Change PIN code")}
            </div>
            <div style={{ fontSize: 12, color: C.muted, textAlign: "center", marginBottom: 20, lineHeight: 1.5 }}>
              {isFirstLogin
                ? (lang === "fi"
                    ? "Sait väliaikaisen PIN-koodin. Suosittelemme vaihtamaan sen omaksi ennen ensimmäistä tapahtumaa."
                    : "You received a temporary PIN. We recommend changing it to your own before your first event.")
                : (lang === "fi"
                    ? "Uusi PIN-koodi tulee käyttöön heti tallennuksen jälkeen."
                    : "The new PIN takes effect immediately after saving.")}
            </div>

            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#ef4444" }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSave}>
              <input
                type="password" inputMode="numeric" maxLength={10}
                value={newPin} onChange={e => setNewPin(e.target.value)}
                placeholder={lang === "fi" ? "Uusi PIN-koodi" : "New PIN code"}
                autoFocus
                style={{ width: "100%", padding: "14px 16px", boxSizing: "border-box", background: C.bg, border: `2px solid ${C.border}`, borderRadius: 12, fontSize: 22, fontWeight: 900, letterSpacing: 8, textAlign: "center", fontFamily: "monospace", outline: "none", marginBottom: 10 }}
              />
              <input
                type="password" inputMode="numeric" maxLength={10}
                value={confirmPin} onChange={e => setConfirmPin(e.target.value)}
                placeholder={lang === "fi" ? "Vahvista PIN-koodi" : "Confirm PIN code"}
                style={{ width: "100%", padding: "14px 16px", boxSizing: "border-box", background: C.bg, border: `2px solid ${newPin && confirmPin && newPin !== confirmPin ? "#ef4444" : C.border}`, borderRadius: 12, fontSize: 22, fontWeight: 900, letterSpacing: 8, textAlign: "center", fontFamily: "monospace", outline: "none", marginBottom: 16 }}
              />
              <button type="submit" disabled={saving || newPin.length < 4 || newPin !== confirmPin} style={{
                width: "100%", padding: "14px 0", borderRadius: 12, border: "none",
                background: newPin.length >= 4 && newPin === confirmPin ? `linear-gradient(135deg, ${C.navy}, #2a5c8f)` : C.border,
                color: newPin.length >= 4 && newPin === confirmPin ? C.white : C.muted,
                fontSize: 15, fontWeight: 700, fontFamily: F.heading, cursor: "pointer", marginBottom: 10,
              }}>
                {saving ? (lang === "fi" ? "Tallennetaan..." : "Saving...") : (lang === "fi" ? "Tallenna uusi PIN →" : "Save new PIN →")}
              </button>
              <button type="button" onClick={onSkip} style={{
                width: "100%", padding: "11px 0", borderRadius: 12, border: `1.5px solid ${C.border}`,
                background: "transparent", color: C.muted, fontSize: 13, fontFamily: F.body, cursor: "pointer",
              }}>
                {lang === "fi" ? "Vaihda myöhemmin" : "Change later"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
